/**
 * 온습도로거 '설정 가이드' 탭 — 요금제 · 업로드 주기/데이터량 · 데이터 타입 · 통신 방식 · 통신망(1NCE · LTE-M) · 배터리 · 장치 설정.
 *
 * 수치의 근거 (2026-11-03 측정):
 *   · 실측 — s31cb_sim.py 로 1시간 측정 더미값을 만들어 격리 수신기까지 31일치를 실제로 송수신했다.
 *     UDP 패킷 크기(JSON) N=12 719B · N=24 1,192B · N=32 1,510B. 통신 바이트 = 페이로드 + IPv4/UDP 헤더 28B,
 *     서버 ACK('Confirm=200') 11B + 28B. 결손 0 (업링크 유실·ACK 유실 시나리오 포함, 재전송으로 복구).
 *   · 계산 — HEX·CoAP·TCP·MQTT·MQTTS 는 위 실측 페이로드에 프로토콜 구조(세그먼트 수·헤더·핸드셰이크)를 더한 모델값.
 *     HEX 바이너리 = 헤더 53B + 항목 8B × N (Dragino 공식 디코더 배치, 매뉴얼 예시 117B 로 검증, 시뮬레이터로 송수신 확인).
 *   · 배터리 — Dragino 배터리 FAQ 의 S31-CB 측정값(2026-11-04 확인): 대기 19.618µA · 업로드 측정 68.176mA×9.015s ·
 *     CLOCKLOG 측정 8.548mA×0.127s · 업링크 1회 UDP 60.97mA×27.4s · TCP 63.44mA×38.1s · MQTT 63.14mA×35.5s · CoAP 63.97mA×35.7s.
 *     수명 = 8,500mAh ÷ (일 소모×365 + 자가방전 2%/년). '신호 약함'은 접속 시간 3배 + 송신 바이트를 1kbps·200mA 로 더한 추정.
 *     UDP·JSON 을 같은 모델로 계산하면 실측과 일치한다(76.2KB).
 *   · 요금 — 1NCE IoT Lifetime Flat, 국내 구매처 MDS테크놀로지: SIM 1장 21,000원(VAT 별도) · 10년 · 500MB, 배송비 5,000원(2026-11-04 사용자 확인).
 *     사용량 · 개통(활성) 정보는 1NCE 홈페이지(포털)에서 관리한다. 국내 통신사 요금제는 2026-11-04 삭제.
 *     데이터 한도는 '10년 500MB' 이므로 월 사용량 × 120개월로 비교한다. 구매 시점에 가격·커버리지를 다시 확인할 것.
 * 수치를 바꾸면 시스템 가이드 14-G 슬라이드와 CLAUDE.md 의 온습도로거 절도 같이 고친다.
 */
import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Col, Descriptions, Row, Space, Steps, Table, Tag, Tooltip, Typography, message } from 'antd';
import {
  CopyOutlined, DownloadOutlined, DashboardOutlined, ClockCircleOutlined, DollarOutlined, BarChartOutlined, CodeOutlined,
  ApiOutlined, ThunderboltOutlined, ShoppingCartOutlined, IdcardOutlined, CheckSquareOutlined, SettingOutlined,
  SafetyCertificateOutlined, RocketOutlined, WifiOutlined, GlobalOutlined, MobileOutlined, SwapOutlined, TeamOutlined,
} from '@ant-design/icons';
import { thloggerAPI } from '../services/api';
import { gray, state } from '../theme/tokens';
import { font } from '../theme';
import { useFillHeight } from '../hooks/useViewportFit';

const { Text } = Typography;
const CLIENT_ZIP = '/downloads/thlogger_client.zip';
const copy = (text: string) => navigator.clipboard?.writeText(text)
  .then(() => message.success('복사되었습니다.')).catch(() => message.error('복사에 실패했습니다.'));

const cell: React.CSSProperties = { fontSize: 12 };
const num: React.CSSProperties = { fontSize: 12, fontVariantNumeric: 'tabular-nums' };
const RecTag = () => <Tag color="green" style={{ margin: 0 }}>권장</Tag>;
const Basis: React.FC<{ kind: '실측' | '계산' | '추정' | '공개요금' }> = ({ kind }) => (
  <Tag style={{ margin: 0, fontSize: 11 }} color={kind === '실측' ? 'blue' : kind === '공개요금' ? 'purple' : 'default'}>{kind}</Tag>
);

/* 1NCE 10년 500MB 대비 사용률 — 월 사용량(kb)을 120개월로 누적해 비교한다.
   색만으로 구분하지 않도록 누적 MB 와 % 를 항상 글자로 적는다 */
const ONCE_MB = 500;
const ONCE_MONTHS = 120;
const tenYearMb = (kbMonth: number) => (kbMonth * ONCE_MONTHS) / 1024;
const Usage: React.FC<{ kb: number }> = ({ kb }) => {
  const mb = tenYearMb(kb);
  const pct = (mb / ONCE_MB) * 100;
  const color = pct > 100 ? '#e5484d' : pct > 85 ? '#eda100' : '#1baf7a';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 170 }}>
      <div style={{ flex: 1, height: 6, background: gray[100], borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ width: `${Math.max(Math.min(pct, 100), 1)}%`, height: '100%', background: color }} />
      </div>
      <span style={{ ...num, width: 92, textAlign: 'right', fontWeight: pct > 100 ? 700 : 500, color: pct > 100 ? state.errorText : gray[700] }}>
        {mb.toFixed(1)}MB · {pct < 10 ? pct.toFixed(1) : Math.round(pct)}%
      </span>
    </div>
  );
};

/* ── 데이터 ─────────────────────────────────────────────── */
const ONCE_PLAN: [string, React.ReactNode][] = [
  ['요금제', '1NCE IoT Lifetime Flat (SIM 1장)'],
  ['요금', 'SIM 1장 21,000원 (VAT 별도) 1회 — 월 정액 · 약정 · 해지 절차 없음'],
  ['배송비', '5,000원 (주문 1회당)'],
  ['기간', '활성화부터 10년 (시작 시점은 구매 시 확인)'],
  ['데이터', '10년 합계 500MB — 월 균등으로 나누면 약 4.2MB'],
  ['SMS', '250건 (10년 합계)'],
  ['망 (한국)', 'LTE-M — KT 망 로밍(Deutsche Telekom 파트너 목록 \'TBC\'). NB-IoT 는 제공되지 않는다 → 구매 전 서면 확인'],
  ['속도', '최대 1Mbps (온습도 업로드에는 무의미)'],
  ['소진 · 만료', '데이터 충전(Top-up) · 10년 연장 — 권장 구성(UDP+HEX)은 10년 약 2.6MB 라 충전할 일이 없다. 조건은 MDS테크놀로지에 확인'],
  ['구매처', 'MDS테크놀로지 (국내 공급사) — 국내 결제 · 세금계산서'],
  ['관리', '사용량 · 개통(활성) 정보는 1NCE 홈페이지(포털)에서 SIM(ICCID)별로 조회 · 관리'],
];
/* MDS테크놀로지 구매가 — SIM 21,000원(VAT 별도) · 배송비 5,000원/주문 */
const SIM_PRICE = 21000;
const SHIP_FEE = 5000;
const ONCE_FLEET = [
  { key: '3', n: '파일럿 2장 + 예비 1장', qty: 3 },
  { key: '22', n: '본구축 20장 + 예비 2장', qty: 22 },
].map(r => ({ ...r, sim: r.qty * SIM_PRICE, total: r.qty * SIM_PRICE + SHIP_FEE }));

const MEASURED = [
  { key: 'a', cfg: '12시간 · N=12', at: 'TDC=43200 · CLOCKLOG …,60,12', up: 62, pkt: 719, ul: 45.3, dl: 2.4, kb: 47.6, gap: 0, rec: false,
    risk: '재전송(REDPT)이 꺼져 있거나 연속 2회 유실되면 12h 결손' },
  { key: 'e', cfg: '8시간 · N=16', at: 'TDC=28800 · CLOCKLOG …,60,16', up: 93, pkt: 876, ul: 82.1, dl: 3.5, kb: 85.6, gap: 0, rec: false,
    risk: '관리자 최적. 유실 1회를 다음 업링크가 복구(N=간격의 2배). 여유 14KB = 추가 업링크 약 15회분' },
  { key: 'b', cfg: '12시간 · N=24', at: 'TDC=43200 · CLOCKLOG …,60,24', up: 62, pkt: 1192, ul: 73.9, dl: 2.4, kb: 76.2, gap: 0, rec: false,
    risk: '배터리 우선 대안. 유실 1회는 다음 업링크가 복구. 여유 24KB = 추가 업링크 약 20회분' },
  { key: 'c', cfg: '24시간 · N=24', at: 'TDC=86400 · CLOCKLOG …,60,24', up: 31, pkt: 1199, ul: 37.1, dl: 1.2, kb: 38.3, gap: 0, rec: false,
    risk: '데이터·배터리 최소. 대신 값이 하루 늦게 보이고 유실 시 재전송에만 의존' },
  { key: 'd', cfg: '24시간 · N=32', at: 'TDC=86400 · CLOCKLOG …,60,32', up: 31, pkt: 1510, ul: 46.5, dl: 1.2, kb: 47.7, gap: 0, rec: true,
    risk: 'N 상한 32 = 32시간 분량. 유실 1회에 16h 결손이 생길 수 있어 재전송 필수' },
];
const MEASURED_BAD = [
  { key: 'e1', cfg: '8시간 · N=16 + 업링크 1회 유실', up: 94, kb: 86.5, gap: 0, note: '재전송으로 복구' },
  { key: 'b1', cfg: '12시간 · N=24 + ACK 2회 유실', up: 64, kb: 78.7, gap: 0, note: '재전송 2회 발생, 중복 측정값은 서버가 제거' },
  { key: 'b2', cfg: '12시간 · N=24 + 업링크 1회 유실', up: 63, kb: 77.4, gap: 0, note: '유실 패킷도 단말은 송신했으므로 과금 대상' },
  { key: 'c1', cfg: '24시간 · N=24 + 업링크 1회 유실', up: 32, kb: 39.5, gap: 0, note: '재전송으로 복구' },
  { key: 'd1', cfg: '24시간 · N=32 + 업링크 1회 유실', up: 32, kb: 49.2, gap: 0, note: '재전송으로 복구' },
];

/* 업로드 간격별 31일 실측 — N 은 '간격의 2배'(유실 1회 자가 복구) 기준과 최소값을 함께 본다 */
/* 월 데이터 = HEX 바이너리 (53 + 8N + 67B) × 월 업로드 수. 수명 = Dragino 측정값 기반, S31-CB 8,500mAh / S31-CB2 19,000mAh (신호 양호) */
const INTERVALS = [
  { key: '4a', iv: '4시간 (1일 6회)', n: 8, rule: '2배', kb: 32.8, fit: 'batt', note: '수명 CB 4.9년 · CB2 9.8년. 세션이 8시간의 2배' },
  { key: '4b', iv: '4시간 (1일 6회)', n: 4, rule: '1배', kb: 27.1, fit: 'risky', note: '유실 복구 여유가 없다 (재전송에만 의존) — N 을 8 로' },
  { key: '6a', iv: '6시간 (1일 4회)', n: 24, rule: '4배', kb: 37.1, fit: 'waste', note: 'N=12 면 충분' },
  { key: '6b', iv: '6시간 (1일 4회)', n: 12, rule: '2배', kb: 25.7, fit: 'ok', note: '수명 CB 6.7년 · CB2 12.8년' },
  { key: '8a', iv: '8시간 (1일 3회)', n: 16, rule: '2배', kb: 22.1, fit: 'ok', note: '확인 시각 우선 대안 — 퇴근 전에 같은 날 한낮 값. 수명 CB 8.2년 · CB2 15.2년' },
  { key: '8b', iv: '8시간 (1일 3회)', n: 24, rule: '3배', kb: 27.8, fit: 'waste', note: 'N 을 키워도 이득이 없다 — 16 이 적정' },
  { key: '12', iv: '12시간 (1일 2회)', n: 24, rule: '2배', kb: 18.5, fit: 'ok', note: '극한 절감 — 수명 CB 10.5년 · CB2 18.7년' },
  { key: '24', iv: '24시간 (1일 1회)', n: 32, rule: '1.3배', kb: 11.2, fit: 'best', note: '운영 적용(2026-11-04) — 매일 08:00. 수명 계산 14.8년(전지 수명이 먼저 한계) · 10년 1.3MB. N 상한 32 = 32시간분이라 유실 1회는 재전송(REDPT)이 복구, 재전송까지 실패하면 최대 16시간 결손' },
];
const FIT: Record<string, { label: string; color: string }> = {
  best: { label: '최적', color: 'green' }, ok: { label: '적합', color: 'blue' }, waste: { label: '과다 동봉', color: 'gold' },
  risky: { label: '복구 여유 없음', color: 'orange' }, batt: { label: '배터리 부담', color: 'orange' },
};

/* 관리자 업무 시각 기준 — 하우스 최저온은 해 뜨기 전(05~06시), 최고온은 한낮(13~15시) */
const SCHEDULES = [
  { key: '8', plan: '8시간 · 00:00 / 08:00 / 16:00', morning: '08:00 도착 — 밤사이 최저온·결로까지 포함', afternoon: '16:00 도착 — 같은 날 한낮 최고온 포함',
    alarm: '08:00 이 빠지면 17:00(근무 중)에 수신 지연 경보', best: false },
  { key: '12a', plan: '12시간 · 08:00 / 20:00', morning: '08:00 도착 — 최저온 포함', afternoon: '한낮 최고온은 20:00 도착 → 다음 날 아침에 확인',
    alarm: '08:00 이 빠지면 21:00(근무 외) 경보 → 다음 날 인지', best: false },
  { key: '12b', plan: '12시간 · 04:00 / 16:00', morning: '04:00 까지만 — 해 뜨기 전 최저온이 빠진다', afternoon: '16:00 도착 — 한낮 최고온 포함',
    alarm: '16:00 이 빠지면 05:00 경보 → 출근 후 인지', best: false },
  { key: '24', plan: '24시간 · 08:00 (적용)', morning: '08:00 도착 — 전날 08:00 ~ 오늘 08:00: 전날 한낮 최고온 · 밤사이 최저온 모두', afternoon: '오늘 한낮 값은 다음 날 08:00 에 도착',
    alarm: '08:00 이 빠지면 같은 날 09:00(근무 중) 수신 지연 경보 — 재전송으로 다음 날 복구', best: true },
];

const SIM_STEPS = [
  { title: '① 커버리지 서면 확인 (구매 전)', who: 'SAPPT → MDS테크놀로지 · 1NCE (이메일)', warn: true,
    items: ['대한민국에서 LTE-M 제공 여부 · 로밍 파트너(KT) — 1NCE 는 커버리지를 보장하지 않는다고 공시하므로 답변을 문서로 남긴다',
      '로밍 중 PSM · eDRX 허용 여부 (참고 — -CB 는 세션 사이 모듈을 꺼 대기 19.6µA 라 수명을 좌우하지 않는다)', '장기(영구) 로밍 제한 · 차단 정책 여부',
      'S31-CB(-CB) 의 KC 전파인증 · LTE-M 지원 밴드가 한국 LTE-M 밴드와 맞는지 — 판매처에 함께 문의',
      '하나라도 불가면 구매 중단 → 통신 방안 재검토'] },
  { title: '② MDS테크놀로지 구매 상담', who: 'SAPPT 담당자 → MDS테크놀로지', warn: false,
    items: ['단가: SIM 1장 21,000원(VAT 별도) · 10년 · 500MB, 배송비 5,000원',
      '함께 확인: 1NCE 홈페이지(포털) 계정 발급 방법 — 사용량 · 개통 정보를 거기서 관리한다',
      '10년 기간 시작 시점(출고 · 활성화 중 언제부터인지) · 데이터 충전/연장 조건 · 세금계산서 발행'] },
  { title: '③ SIM 주문', who: 'SAPPT 담당자', warn: false,
    items: ['파일럿: 2장 + 예비 1장(63,000원 + 배송 5,000원) → 판정 후 본구축 20장 + 예비 2장(462,000원 + 배송 5,000원) · 모두 VAT 별도',
      'SIM 형태: 3-in-1 컷아웃 — S31-CB 슬롯 크기(나노)로 분리해 쓴다', '배송비는 주문마다 붙으므로 본구축 분은 한 번에 주문',
      '대안: S31-CB \'-1T\' 버전은 1NCE SIM 이 장착된 채 출고된다(ThingsEye 프리셋 → AT 명령으로 SAPPT 서버로 바꾼다). 이 경우 SIM 은 따로 사지 않고, 포털 등록 · 10년 시작 시점을 판매처에 확인'] },
  { title: '④ 수령 · 활성 확인', who: 'SAPPT', warn: false,
    items: ['1NCE 홈페이지(포털)에서 ICCID 별 개통(활성) 상태 · 10년 기간 시작일 · 데이터 잔량 500MB 확인',
      '포털 사용량 경보 설정 (SIM 별 월 사용량이 예상보다 크면 통지)', '선택: IMEI 잠금 — 다른 단말에서 SIM 을 쓰지 못하게 한다(포털 기능 확인)'] },
  { title: '⑤ 단말 설정 · 첫 수신 확인', who: 'SAPPT', warn: false,
    items: ["lab 온습도로거 → 장치 대장에 IMEI 등록 (통신사/망 '1NCE', 회선번호 칸에 ICCID)",
      "사용자 가이드 'AT 명령 · 블루투스' 의 권장 명령(AT+IOTMOD=0 LTE-M · AT+APN=iot.1nce.net 포함) → 08:00 무렵 ATZ",
      "업링크 로그에서 수신 · 신호(dBm) 확인, 1NCE 포털에서 접속 망(KT) 확인, 알림센터 '첫 수신' 알림"] },
  { title: '⑥ 파일럿 2~3주 → 20장 추가 주문', who: 'SAPPT', warn: false,
    items: ['수신율(결손 0) · 신호 · 배터리 전압 기울기 · 포털 사용량을 계산값(UDP+HEX 월 22KB)과 비교',
      '이상 없으면 본구축 SIM 을 한 번에 주문', '로밍 접속이 불안정한 하우스는 설치 위치부터 조정'] },
];

const PAYLOADS = [
  { key: 'hexb', name: 'HEX (바이너리 전송)', at: 'AT+PRO=2,0', n24: '245B', m12: 18.5, m24: 9.3, basis: '실측' as const, decoder: '지원 (2026-11-04)', rec: true,
    pros: 'JSON 대비 데이터 -79%. 시각이 Unix epoch 라 시간대 가정이 없다. 패킷이 짧아 약한 신호에서 송신 시간이 준다', cons: '사람이 바로 읽을 수 없다 — 서버가 해석해 업링크 로그 · 측정 데이터로 보여준다' },
  { key: 'hexs', name: 'HEX (16진 문자열 전송)', at: 'AT+PRO=2,0', n24: '490B', m12: 33.1, m24: 16.5, basis: '계산' as const, decoder: '지원 (같은 디코더)', rec: false,
    pros: '단말이 문자열로 보내도 서버가 그대로 받는다', cons: '바이너리의 2배. 어느 쪽으로 오는지는 파일럿 첫 패킷으로 확인(서버는 둘 다 받는다)' },
  { key: 'json', name: 'JSON', at: 'AT+PRO=2,5', n24: '1,192B', m12: 76.2, m24: 38.3, basis: '실측' as const, decoder: '지원', rec: false,
    pros: '사람이 읽을 수 있다. 단말의 다운링크 확인 응답(Downklink_Ack)은 JSON 모드에서만 온다', cons: '크기가 HEX 바이너리의 약 5배. "time" 필드에 시간대 표기가 없다' },
];

/* 데이터는 HEX 바이너리 · N=24 기준, 세션 전력은 Dragino 배터리 FAQ 의 S31-CB 측정값 */
const PROTOCOLS = [
  { key: 'udp', name: 'UDP', at: 'AT+PRO=2,0', session: '송신 1패킷 + ACK 1패킷', ovh: '28B + ACK 39B', m12: 18.5, m24: 9.3, basis: '실측' as const,
    battery: '61.0mA × 27.4초 = 0.46mAh', server: '지원 (가동 중)', rec: true, note: '세션이 가장 짧다(Dragino 실측). 신뢰성은 ACK + 재전송(REDPT) + 로그 중첩으로 확보' },
  { key: 'coap', name: 'CoAP', at: 'AT+PRO=1,0', session: 'UDP + CoAP 헤더·확인응답', ovh: '≈ 44B + ACK 32B', m12: 19.1, m24: 9.5, basis: '계산' as const,
    battery: '64.0mA × 35.7초 = 0.63mAh', server: '미지원', rec: false, note: '데이터는 UDP 와 같지만 세션이 8초 길다(+37%). 수신 서버도 따로 둬야 한다' },
  { key: 'tcp', name: 'TCP', at: 'AT+PRO=4,0', session: '연결 3 + 데이터 3 + 종료 4 세그먼트', ovh: '≈ 532B', m12: 46.2, m24: 23.1, basis: '계산' as const,
    battery: '63.4mA × 38.1초 = 0.67mAh', server: '미지원', rec: false, note: '세션이 가장 길다(+45%). MQTT 의 이점(인증·토픽) 없이 연결 비용만 든다' },
  { key: 'mqtt', name: 'MQTT', at: 'AT+PRO=3,0', session: 'TCP + CONNECT·PUBLISH·PUBACK·DISCONNECT', ovh: '≈ 788B', m12: 61.4, m24: 30.7, basis: '계산' as const,
    battery: '63.1mA × 35.5초 = 0.62mAh', server: '수신기 지원 · 브로커 필요', rec: false, note: "Dragino 문서: 'MQTT 는 UDP/CoAP 보다 전력 소모가 훨씬 크다'. 데이터 3.3배 · 세션 +34%" },
  { key: 'mqtts', name: 'MQTTS', at: 'AT+PRO=3,0 + AT+TLSMOD=1,1', session: 'MQTT + TLS 핸드셰이크(인증서)', ovh: '≈ 5.8KB', m12: 359.1, m24: 179.6, basis: '계산' as const,
    battery: '≈ 0.9mAh (MQTT×1.5 추정)', server: '수신기 지원 · 브로커·인증서 필요', rec: false, note: '데이터 19배(10년 약 42MB) · 약한 신호에서 TLS 왕복이 반복돼 배터리 최악' },
];

/* Dragino 측정값 기반 (신호 양호) — 머리 주석의 계산법. 괄호는 신호 약함 추정 */
const BATTERY = [
  { key: '1', cfg: 'UDP+HEX · 24시간 · N=32', day: '1.11mAh', life: '10년+ (8.7)', note: '운영 적용 — 계산 14.8년, 전지 자체 수명(약 10년)이 먼저 한계', rec: true },
  { key: '2', cfg: 'UDP+HEX · 12시간 · N=24', day: '1.75mAh', life: '10.5년 (5.4)', note: '극한 절감 — 유실 1회 자가 복구를 지키는 가장 긴 간격' },
  { key: '2b', cfg: 'UDP+HEX · 8시간 · N=16', day: '2.38mAh', life: '8.2년 (3.9)', note: '확인 시각 우선 대안 — 퇴근 전에 같은 날 한낮 값' },
  { key: '3', cfg: 'MQTT+HEX · 8시간', day: '2.86mAh', life: '7.0년 (2.8)', note: 'TCP 연결 · MQTT 왕복만큼 세션이 8초 길다' },
  { key: '4', cfg: 'MQTTS+HEX · 8시간', day: '≈ 3.8mAh', life: '5.5년 (1.3)', note: 'TLS 핸드셰이크가 세션마다 반복' },
  { key: '5', cfg: 'UDP · 1시간마다 업로드', day: '15.7mAh', life: '1.4년', note: '측정 주기와 업로드 주기를 같게 하면 안 되는 이유' },
];
/* 데이터 타입 × 통신 방식 (8시간 · N=16). 세션(양호) = Dragino 측정 업링크 + 업로드 측정 0.171mAh.
   신호 약함 = 접속 시간 3배 + 송신 바이트 × 8 ÷ 1kbps × 200mA (추정). 수명 = 8,500mAh ÷ (일 소모×365 + 170) */
const COMBOS = [
  { key: 'uh', combo: 'UDP + HEX (적용)', bytes: 248, kb: 22.1, good: '0.64mAh · 8.2년', weak: '1.67mAh · 3.9년', rec: true, note: '배터리 · 데이터 모두 최소' },
  { key: 'uj', combo: 'UDP + JSON (이전)', bytes: 943, kb: 84.0, good: '0.64mAh · 8.2년', weak: '1.98mAh · 3.4년', rec: false, note: '신호가 좋으면 같다(송신은 27초 중 1초 미만). 약하면 HEX 가 +15%' },
  { key: 'ch', combo: 'CoAP + HEX', bytes: 264, kb: 23.5, good: '0.80mAh · 6.9년', weak: '2.19mAh · 3.1년', rec: false, note: '세션이 UDP 보다 8초 길다' },
  { key: 'th', combo: 'TCP + HEX', bytes: 780, kb: 69.5, good: '0.84mAh · 6.7년', weak: '2.53mAh · 2.7년', rec: false, note: '연결 수립 · 종료 왕복' },
  { key: 'mh', combo: 'MQTT + HEX', bytes: 1036, kb: 92.3, good: '0.79mAh · 7.0년', weak: '2.50mAh · 2.8년', rec: false, note: 'HEX 로 줄인 바이트를 MQTT 연결 왕복이 다시 쓴다' },
  { key: 'mj', combo: 'MQTT + JSON', bytes: 1731, kb: 154.3, good: '0.79mAh · 7.0년', weak: '2.81mAh · 2.5년', rec: false, note: '' },
  { key: 'ms', combo: 'MQTTS + HEX', bytes: 6048, kb: 538.9, good: '1.10mAh · 5.5년', weak: '5.66mAh · 1.3년', rec: false, note: '약한 신호에서 TLS 핸드셰이크가 반복 전송돼 급격히 나빠진다' },
];

const BATTERY_FACTORS = [
  ['업로드 횟수(TDC)', '가장 큰 요인. 1시간 측정은 센서만 잠깐 켜져 전력이 미미하고, 통신 세션 1회가 측정 수백 번보다 비싸다'],
  ['통신 방식', 'UDP < CoAP < TCP·MQTT < MQTTS. 세션마다 무선이 켜져 있는 시간이 길수록 소모가 커진다'],
  ['신호 세기', '약하면 송신 출력·재시도가 늘어 2~3배 소모. 업링크 로그의 신호가 -105dBm 이하이면 본체 위치·높이 조정 (LTE-M 은 NB-IoT 보다 커버리지가 얕아 더 중요하다)'],
  ['대기 전류', 'Dragino 실측 19.6µA = 하루 0.47mAh. 8시간 간격에서 소모의 20%, 24시간이면 42% — 간격을 늘릴수록 대기가 바닥을 정한다. 세션 사이 모듈이 꺼져 있어 망의 PSM 허용 여부와 무관하다'],
  ['재전송(REDPT)', '켜 두는 것이 이득. 유실 때만 동작하고, 끄면 결손이 복구되지 않는다'],
  ['경보 업링크', 'AT+SHTEMP/SHHUM 경보를 켜면 임계 넘을 때마다 추가 세션이 생긴다. 하우스 한낮 고온이면 매일 발생 → 끄고 서버에서 판정'],
  ['온도', 'Li/SOCl2 전지는 저온에서 용량이 준다. 겨울 하우스 야간 영하가 길면 수명이 짧아진다'],
];

const won = (v: number) => `${v.toLocaleString('en-US')}원`;

/* ── 섹션 ───────────────────────────────────────────────── */
const SummarySection: React.FC = () => (
  <>
    <Alert type="success" showIcon style={{ marginBottom: 12 }}
      message={<span><b>적용 구성 (2026-11-04 확정)</b> — <b>1NCE SIM (10년 · 500MB)</b> · LTE-M · <b>UDP</b> · <b>HEX</b> · 1시간 측정 · <b>1일 1회 08:00 업로드</b> · 로그 <b>32건</b> 동봉 · 재전송 켬 · 경보 · GPS · 인터럽트 끔</span>}
      description={<span style={{ fontSize: 12 }}>HEX 월 <b>11.2KB</b> → 10년 약 <b>1.3MB</b>, 500MB 의 0.26%. 배터리(Dragino 실측 기반, 신호 양호)는 S31-CB 계산 <b>14.8년</b> · S31-CB2 24.2년으로 전지 자체 수명(약 10년)이 먼저 한계다.
        출근(08시)에 전날 한낮 최고온과 밤사이 최저온을 함께 본다. 32건은 동봉 상한이라 간격(24건)의 1.3배 — 유실 1회는 재전송이 복구한다. 같은 날 한낮 값을 퇴근 전에 봐야 하면 8시간(00·08·16시) 대안.</span>} />
    <Row gutter={12}>
      {[
        ['요금제', '1NCE 10년', '21,000원 1회 · 500MB', 'VAT 별도 · MDS테크놀로지 · 월 정액 없음'],
        ['데이터 (1일 1회)', '1.3MB', '10년 누적 · UDP+HEX · N=32', '월 11.2KB · 500MB 의 0.26%'],
        ['배터리 (1일 1회)', '10년+', 'S31-CB 계산 14.8년 · 신호 양호', '약한 신호 8.7년 · S31-CB2 16.0년'],
        ['20대 통신비', '467,000원', '1회 · 22장 + 배송 (VAT 별도)', '10년 기준 1대 연 2,100원'],
      ].map(([t, v, sub, n]) => (
        <Col span={6} key={t}>
          <Card size="small" style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 12, color: gray[500] }}>{t}</div>
            <div style={{ fontSize: 20, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{v}</div>
            <div style={{ fontSize: 12, color: gray[600] }}>{sub}</div>
            <div style={{ fontSize: 11, color: gray[400], marginTop: 2 }}>{n}</div>
          </Card>
        </Col>
      ))}
    </Row>
    <Card size="small" title="판단 근거 한눈에">
      <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, lineHeight: 1.9, color: gray[700] }}>
        <li><b>전송 간격</b>: 1NCE 는 데이터가 충분해(10년 500MB) 간격을 정하는 것은 <b>배터리</b>와 확인 시각이다. <b>1일 1회(08:00)</b>는 세션이 가장 적어 계산 14.8년 — 전지 수명이 먼저 한계다. 8시간은 8.2년. 4시간은 약 5년.</li>
        <li><b>전송 시각</b>: 하우스 최저온은 해 뜨기 전, 최고온은 한낮이다. 08:00 한 번이면 아침 확인 때 전날 한낮 최고온과 밤사이 최저온이 함께 도착하고, 빠지면 같은 날 09:00 에 경보가 뜬다(근무 시간).</li>
        <li><b>요금</b>: SIM 1장 21,000원(VAT 별도, MDS테크놀로지)으로 10년. 모든 권장안이 10년 500MB 의 2% 안팎이라 충전할 일이 없다. 사용량 · 개통 정보는 1NCE 홈페이지에서 관리한다.</li>
        <li><b>데이터 타입</b>: <b>HEX(바이너리)</b>. JSON 대비 -79% 이고 약한 신호에서 송신 시간이 줄어 배터리 +15%. 서버가 Dragino 공식 배치로 해석한다(2026-11-04). 전송 형태 · ACK 는 파일럿 첫 장치에서 확인.</li>
        <li><b>통신 방식</b>: UDP. MQTT·MQTTS 도 데이터는 500MB 안이지만 연결·암호화 왕복으로 배터리 수명이 짧아진다. 온습도는 민감정보가 아니다.</li>
        <li><b>통신망</b>: 1NCE 는 한국에서 <b>LTE-M(KT 망 로밍)</b>만 제공한다. LTE-M 은 NB-IoT 보다 커버리지가 약 8dB 얕아 설치 위치의 신호 확인이 더 중요하다.</li>
        <li><b>배터리</b>: 데이터 크기가 아니라 <b>통신 세션 횟수 × 방식</b>이 정한다(UDP 1회 27.4초, Dragino 실측). 1일 1회 14.8년 · 8시간 8.2년 (S31-CB2 는 2.2배 용량으로 24.2 · 15.2년).</li>
        <li><b>구매 전 확인</b>: 한국 LTE-M 로밍 · 장기 로밍 제한을 1NCE 에 <b>서면으로</b> 확인하는 것이 가장 먼저다('1NCE 구매 절차' ①).</li>
      </ul>
      <div style={{ fontSize: 11.5, color: gray[500], marginTop: 8 }}>
        <Basis kind="실측" /> 시뮬레이터 더미값을 수신기까지 31일치 실제 송수신 · <Basis kind="계산" /> 실측 페이로드 + 프로토콜 구조 · <Basis kind="추정" /> 배터리 모델 · <Basis kind="공개요금" /> MDS테크놀로지 구매가(구매 시 재확인)
      </div>
    </Card>
  </>
);

const IntervalSection: React.FC = () => (
  <>
    <Card size="small" title="전송 간격 — 1시간 측정 · 31일 실측 · 1NCE 10년 500MB 기준" style={{ marginBottom: 12 }} extra={<Basis kind="실측" />}>
      <Table size="small" pagination={false} rowKey="key" dataSource={INTERVALS} columns={[
        { title: '업로드 간격', dataIndex: 'iv', width: 130, render: (v: string) => <b style={cell}>{v}</b> },
        { title: '동봉 N', dataIndex: 'n', width: 80, render: (v: number, r: any) => <span style={num}>{v} ({r.rule})</span> },
        { title: '월 데이터', dataIndex: 'kb', width: 80, render: (v: number) => <b style={num}>{v}KB</b> },
        { title: '10년 누적 / 500MB', key: 'u', width: 200, render: (_: any, r: any) => <Usage kb={r.kb} /> },
        { title: '판정', dataIndex: 'fit', width: 110, render: (v: string) => <Tag color={FIT[v].color} style={{ margin: 0 }}>{FIT[v].label}</Tag> },
        { title: '설명', dataIndex: 'note', render: (v: string) => <span style={{ ...cell, color: gray[600] }}>{v}</span> },
      ]} />
      <div style={{ fontSize: 12.5, lineHeight: 1.85, color: gray[700], marginTop: 10 }}>
        <b>적용: 1일 1회(08:00) · N=32</b> (2026-11-04 결정). 1NCE 10년 500MB 에서는 어느 간격도 데이터가 모자라지 않아(4시간도 10년 4MB)
        간격은 <b>배터리(세션 수)</b>와 <b>확인 시각</b>으로 정한다 — 1일 1회는 세션이 가장 적고(계산 14.8년) 08:00 확인에 전날 전체가 도착한다.
        동봉 건수는 간격의 2배가 원칙이지만 1일 1회는 48건이 필요해 상한 32건을 쓴다 — 유실 1회는 재전송(REDPT)이 복구하고, 재전송까지 실패하면 최대 16시간이 빈다.
      </div>
    </Card>
    <Row gutter={12}>
      <Col span={16}>
        <Card size="small" title="관리자 업무 시각 기준 전송 시간대" style={{ marginBottom: 12 }}>
          <Table size="small" pagination={false} rowKey="key" dataSource={SCHEDULES} columns={[
            { title: '전송 시각', dataIndex: 'plan', width: 200, render: (v: string, r: any) => <Space size={4}><b style={cell}>{v}</b>{r.best && <RecTag />}</Space> },
            { title: '아침 확인 (08:30)', dataIndex: 'morning', render: (v: string) => <span style={cell}>{v}</span> },
            { title: '퇴근 전 확인 (16:30)', dataIndex: 'afternoon', render: (v: string) => <span style={cell}>{v}</span> },
            { title: '수신 지연 경보', dataIndex: 'alarm', render: (v: string) => <span style={{ ...cell, color: gray[600] }}>{v}</span> },
          ]} />
          <div style={{ fontSize: 12, color: gray[600], marginTop: 8, lineHeight: 1.8 }}>
            하우스 기온은 해 뜨기 직전(05~06시)에 가장 낮고 한낮(13~15시)에 가장 높다. 약해·저온 피해 판단은 이 두 극값이 기준이라
            <b> 아침 확인 전에 최저온이, 퇴근 전 확인 전에 최고온이 도착</b>해야 한다. 1일 1회(08:00)는 아침 확인에 두 극값이 모두 오지만 같은 날 한낮 값은 다음 날 본다 — 퇴근 전 확인이 꼭 필요하면 8시간(00·08·16시)으로 바꾼다.
          </div>
        </Card>
      </Col>
      <Col span={8}>
        <Card size="small" title="전송 시각을 맞추는 방법" style={{ marginBottom: 12 }}>
          <ol style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, lineHeight: 1.85, color: gray[700] }}>
            <li>업로드 간격(TDC)은 <b>마지막 재시작 시각부터</b> 센다. 시계 시각을 직접 지정하는 설정은 확인되지 않았다(파일럿에서 확인).</li>
            <li>설치 마지막 단계의 <Text code>ATZ</Text>(또는 버튼 재시작)를 <b>08:00 무렵</b>에 하면 이후 08·16·00시에 올라온다.</li>
            <li>20대를 같은 분에 재시작하지 말고 <b>1~2분씩 어긋나게</b> 한다 — 서버·망 부하가 분산된다.</li>
            <li>단말 시계는 조금씩 밀린다. 업링크 로그의 수신 시각이 30분 이상 밀리면 다음 점검 때 다시 <Text code>ATZ</Text> 한다.</li>
            <li>정시 측정: <Text code>CLOCKLOG</Text> 둘째 값을 <Text code>0</Text> 으로 주면 매시 정각에 기록된다(기본 65535 는 접속 시점 기준). 파일럿에서 확인 후 적용한다.</li>
          </ol>
        </Card>
      </Col>
    </Row>
  </>
);

const SimSection: React.FC = () => (
  <>
    <Alert type="warning" showIcon style={{ marginBottom: 12 }}
      message={<span><b>가장 먼저 확인할 것</b> — 1NCE 가 한국에서 <b>LTE-M(KT 망 로밍)</b>을 실제로 제공하는지. NB-IoT 는 한국에서 제공되지 않는다.</span>}
      description={<span style={{ fontSize: 12 }}>Deutsche Telekom 로밍 파트너 목록에는 대한민국 KT LTE-M 이 'TBC' 로만 올라 있고, 1NCE 는 커버리지를 보장하지 않는다고 공시한다.
        ①의 답변을 이메일로 받아 보관한 뒤 주문한다. 국내 통신사 가입 서류 · 회선 개통 절차는 필요 없다.</span>} />
    <Row gutter={12}>
      <Col span={9}>
        <Card size="small" title="구매 · 활성화 흐름도" style={{ marginBottom: 12 }}>
          <Steps direction="vertical" size="small" current={0} items={SIM_STEPS.map(st => ({
            title: <span style={{ fontSize: 13, fontWeight: 700 }}>{st.title}</span>,
            description: <span style={{ fontSize: 12, color: gray[500] }}>{st.who}</span>,
          }))} />
          <div style={{ marginTop: 8, padding: '8px 10px', background: gray[50], borderRadius: 6, fontSize: 12, color: gray[700], lineHeight: 1.8 }}>
            ① 에서 <b>불가</b> 또는 <b>답변 없음</b>이면 주문하지 않는다 — 로밍이 열리지 않은 SIM 은 단말에 꽂아도 접속되지 않는다.
          </div>
        </Card>
      </Col>
      <Col span={15}>
        {SIM_STEPS.map(st => (
          <Card key={st.title} size="small" style={{ marginBottom: 8, borderLeft: `3px solid ${st.warn ? '#eda100' : '#4f46e5'}` }}
            title={<Space size={6} wrap><span style={{ fontSize: 13 }}>{st.title}</span><Tag style={{ margin: 0 }}>{st.who}</Tag></Space>}>
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, lineHeight: 1.8, color: gray[700] }}>
              {st.items.map(it => <li key={it}>{it}</li>)}
            </ul>
          </Card>
        ))}
      </Col>
    </Row>
  </>
);

const PURCHASE_STEPS = [
  { key: 's1', title: '1. 입고 검사', when: '수령 당일', who: '담당 연구원',
    items: ['수량·모델 확인: S31-CB(외장 프로브 3m) / S31B-CB(내장) 구분',
      '라벨의 IMEI 15자리·AT 비밀번호를 기록하고 사진을 남긴다 (장치 대장 비고·라벨 분실 대비)',
      'KC 인증 표시 확인 (없으면 사용 전에 판매처에 인증 여부 확인)',
      '외관·프로브 케이블·방수 패킹 상태, 전원 투입 방식(점퍼 또는 버튼)을 동봉 설명서로 확인'],
    done: '전 수량 IMEI 기록 완료 · 불량 0' },
  { key: 's2', title: '2. 장치 대장 사전 등록', when: '입고 당일', who: '담당 연구원 (lab)',
    items: ['lab 온습도로거 → 장치 대장 → 등록: IMEI, 장치명(예: PILOT-01), 모델, 측정 60분, 업로드 1일 1회(기본값)',
      '설치 지점은 설치할 때 정한다 — 설치 지점 탭에서 지점 등록 후 기기 설치',
      '등록해야 업링크가 저장된다 — 미등록이면 거절 로그에만 남는다'],
    done: '장치 대장에 전 수량 등록 (상태: 수신 대기)' },
  { key: 's3', title: '3. SIM 장착 · 단말 설정', when: 'SIM 수령 후', who: '담당 연구원',
    items: ["사용자 가이드 '유심 장착 · 기기 열기' 순서대로 나노 SIM 장착 (전원 점퍼를 뺀 상태에서) → 전원 투입",
      '휴대폰 앱(Devices.tool / Dragino) 블루투스로 접속 (라벨의 AT PIN 6자리)',
      "사용자 가이드 'AT 명령 · 블루투스' 의 권장 명령을 순서대로 입력 (복사 버튼 — AT+IOTMOD=0 · AT+APN=iot.1nce.net 포함)",
      'AT+CFG 로 설정값을 출력해 캡처 → 장치 대장 비고에 펌웨어 버전 기록',
      'ATZ 로 재시작'],
    done: '설정 출력이 권장값과 일치' },
  { key: 's4', title: '4. 사무실 수신 시험', when: '1~3일', who: '담당 연구원 + 시스템 담당',
    items: ['버튼을 눌러 즉시 업로드 → 업링크 로그에 1~2분 안에 보이는지, 알림센터 "첫 수신" 알림',
      '확인 항목: IMEI 일치 · 신호(dBm) · 배터리(V) · 측정값이 실내 온습도와 비슷한지',
      '업링크 로그를 펼쳐 해석 표의 측정 시각이 실제 시각(KST)과 맞는지 확인 — HEX 는 UTC 기준 시각을 보내고 서버가 KST 로 바꿔 보여 준다',
      '빠른 확인이 필요하면 하루만 AT+TDC=3600(1시간) → 끝나면 86400 으로 되돌린다 (데이터 약 1KB/회)',
      '재전송 시험: 시스템 담당이 수신기를 5분 멈춘 사이 버튼 업로드 → 다시 켠 뒤 다음 업로드에 그 데이터가 들어오는지'],
    done: '업로드 3회 이상 수신 · 결손 0 · 거절 로그 0 · 시간대 확정' },
  { key: 's5', title: '5. 실기기 기준 확정 (개발)', when: '시험 직후', who: '시스템 담당',
    items: ['실제 패킷으로 확인: HEX 전송 형태(바이너리 · 문자열) · ACK(Confirm=200) 동작 · 재전송 범위',
      'CLOCKLOG 둘째 값 0(정시 측정) 동작 확인 후 설정 가이드 AT 명령에 반영',
      'HEX 다운링크(원격 주기 변경)가 실기기에 반영되는지 — 장치 대장 주기 변경 상태가 \'반영됨\' 이 되는지',
      '실측 패킷 크기로 월 데이터량 재계산 → 설정 가이드 수치 갱신'],
    done: '설정 가이드 · AT 명령 확정본 게시' },
  { key: 's6', title: '6. 현장 설치', when: '파일럿 하우스', who: '담당 연구원',
    items: ['프로브: 작물 생장점 높이, 직사광선이 닿지 않게 차광(복사 차폐) — 햇빛에 노출되면 실제보다 5~10℃ 높게 나온다',
      '관수·약제 살포 물이 직접 닿지 않는 곳, 환기창 바로 앞은 피한다',
      '본체: 그늘진 철골 상단 (높을수록 신호가 좋다), 케이블은 늘어지지 않게 고정',
      '설치 직후 버튼 업로드 → 업링크 로그의 신호 확인: -105dBm 이하이면 본체 위치·높이를 옮긴다',
      '08:00 무렵 ATZ → 이후 매일 08:00 업로드',
      '설치 지점 탭에서 지점(코드 · 위치 · 높이)을 등록하고 이 기기를 설치, 본체에 지점 코드 · 장치명 라벨 부착'],
    done: '현장 업로드 수신 · 신호 -105dBm 초과' },
  { key: 's7', title: '7. 시험 배정', when: '설치 당일', who: '담당 연구원 (lab)',
    items: ['설치 지점 탭 → 시험 배정: 시험일정(또는 시험번호)과 시작일 지정 — 지점에 걸어 두면 기기를 바꿔도 이어진다',
      '작기가 끝나면 이력에서 배정 종료(오늘로 종료) → 다른 시험이면 새 배정 추가',
      "측정 데이터 탭 → 왼쪽에서 지점 선택 → 기간 '시험 기간' → 엑셀로 성적서 환경조건 근거 출력"],
    done: '배정 기간의 측정 데이터·엑셀 출력 확인' },
  { key: 's8', title: '8. 파일럿 운영 (2~3주)', when: '설치 후 2~3주', who: '담당 연구원 + 시스템 담당',
    items: ['매일: 수신 지연 경보 없음 · 결손 0 · 이상값(센서 오류) 여부',
      '매주: 배터리 전압 추세 기록, 1NCE 포털 사용량으로 월 데이터 예상(UDP+HEX 1일 1회 약 11KB 기준)과 비교',
      '하우스별 신호 차이 기록 → 20대 설치 위치 기준 마련'],
    done: '아래 확대 판정 기준 충족' },
  { key: 's9', title: '9. 확대 판정 → 20대 발주', when: '파일럿 종료', who: '책임자',
    items: ['연속 7일 이상 수신, 결손 0 (재전송 복구 포함)',
      '신호 -105dBm 초과 (대상 하우스 대부분)',
      '10년 환산 데이터 500MB 이하 (1NCE 포털 사용량 기준)',
      '배터리 전압 하강률로 추정한 수명 5년 이상 (계산 14.8년 · 약한 신호 8.7년)',
      '불충족 시: 간격 12시간으로 조정 · LTE-M 검토 · 설치 위치 변경 후 재시험'],
    done: '판정 기록 후 나머지 단말·회선 발주' },
  { key: 's10', title: '10. 20대 구축 · 운영 전환', when: '발주 후', who: '담당 연구원 + 시스템 담당',
    items: ['1~3 단계를 일괄 진행 (장치명 규칙: 하우스코드-로거, 예 H01-LOGGER)',
      '현장 ATZ 는 장치마다 1~2분씩 어긋나게 → 업로드가 한꺼번에 몰리지 않는다',
      'Docker 샘플 로거 중지(docker compose stop sappt-thlogger-demo) · DEMO·SIM 시험 장치 비활성화',
      '월 1회 점검: 배터리 3.0V 미만 장치 교체 준비, 시각 밀림(30분 이상) 재정렬, 사용량 확인'],
    done: '20대 수신 정상 · 시험 장치 정리 완료' },
];

const PurchaseSection: React.FC = () => (
  <Row gutter={12}>
    <Col span={7}>
      <Card size="small" title="전체 흐름" style={{ marginBottom: 12 }}>
        <Steps direction="vertical" size="small" current={-1} items={PURCHASE_STEPS.map(st => ({
          title: <span style={{ fontSize: 12.5, fontWeight: 700 }}>{st.title}</span>,
          description: <span style={{ fontSize: 11.5, color: gray[500] }}>{st.when} · {st.who}</span>,
        }))} />
      </Card>
      <Card size="small" title="준비물">
        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, lineHeight: 1.8, color: gray[700] }}>
          <li>1NCE SIM (활성 확인 · 나노 크기로 분리)</li>
          <li>스마트폰(BLE 설정 앱) 또는 USB-TTL 케이블</li>
          <li>라벨 프린터·케이블 타이·차광 덮개(복사 차폐)</li>
          <li>lab 계정 (온습도로거 등록 권한)</li>
        </ul>
      </Card>
    </Col>
    <Col span={17}>
      {PURCHASE_STEPS.map(st => (
        <Card key={st.key} size="small" style={{ marginBottom: 8 }}
          title={<Space size={6} wrap><span style={{ fontSize: 13 }}>{st.title}</span><Tag style={{ margin: 0 }}>{st.when}</Tag><Tag style={{ margin: 0 }}>{st.who}</Tag></Space>}>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, lineHeight: 1.8, color: gray[700] }}>
            {st.items.map(it => <li key={it}>{it}</li>)}
          </ul>
          <div style={{ marginTop: 6, fontSize: 12, color: state.successText }}>✔ 완료 기준: {st.done}</div>
        </Card>
      ))}
    </Col>
  </Row>
);

/* 구매 구성 — 완제품 / 내장형 / 배터리·프로브·차폐 별도 구매 */
const BUY_OPTIONS = [
  { key: 'full', name: 'S31-CB 완제품 (외장 프로브 3m)', rec: true, fit: '파일럿 · 20대 본구축',
    pros: ['배터리(8,500mAh)·SHT31 프로브·방수 케이스가 조립·검증된 상태로 온다 — 받은 날 바로 시험할 수 있다',
      '프로브를 작물 높이에, 본체를 신호 좋은 높은 곳에 따로 둘 수 있다 (하우스에서 가장 큰 장점)',
      '제조사 보증·펌웨어·인증이 완제품 단위로 유지된다 — KC 인증 확인도 완제품 모델로 한다'],
    cons: ['배터리를 바꿀 때 정품 팩(ER26500 + SPC1520 형식)을 맞춰야 한다',
      '프로브가 케이블 일체형이면 프로브 고장 시 교체 방법을 판매처에 확인해야 한다'] },
  { key: 'b', name: 'S31B-CB 완제품 (센서 내장)', rec: false, fit: '실내 · 그늘진 곳',
    pros: ['외장 케이블이 없어 설치가 가장 간단하다', '프로브 케이블 손상 위험이 없다'],
    cons: ['본체 위치 = 측정 위치라 작물 높이(낮은 곳)에 두면 신호가 나빠지고, 높이 두면 작물 환경과 다른 값을 잰다',
      '본체가 햇빛을 받으면 케이스가 데워져 실제보다 높게 잰다 — 하우스에는 부적합',
      '결로·농약 살포에 본체가 그대로 노출된다'] },
  { key: 'batt', name: '배터리 별도 구매 (교체·예비)', rec: false, fit: '신호 약한 하우스는 4~5년 뒤 교체 · 예비 1~2개',
    pros: ['Li/SOCl2 는 자가방전이 연 1% 미만이라 예비품을 오래 보관할 수 있다',
      '국내에서 같은 규격 셀을 조달할 수 있어 장기적으로 저렴할 수 있다'],
    cons: ['일반 ER26500 셀 단독은 안 된다 — 통신 순간 큰 전류를 SPC(하이브리드 커패시터)가 받쳐 준다. 빠지면 전압이 꺼져 재부팅·통신 실패',
      '커넥터·크기가 정품 팩과 다르면 장착이 안 된다 → 정품 팩 또는 같은 구성품을 쓴다',
      '리튬 1차전지는 항공 운송 제한이 있어 해외 단품 주문 배송이 늦거나 막힐 수 있다',
      '처음부터 20대분을 사 둘 필요는 없다 — 파일럿 전압 추세로 교체 시점을 본 뒤 구매'] },
  { key: 'probe', name: '프로브 별도 구매 (교체·고정밀)', rec: false, fit: '예비 10% · 습도 정확도 관리',
    pros: ['하우스는 농약 살포·결로로 습도 센서가 빨리 오염된다 — 프로브만 바꾸면 본체·배터리는 그대로 쓴다',
      '연 1회 기준 습도계와 비교해 벗어난 프로브만 교체해 정확도를 유지한다'],
    cons: ['펌웨어가 SHT31(I2C) 을 전제로 한다 — SHT35·SHT45 같은 다른 센서는 호환 확인 전에는 쓰지 않는다',
      '케이블을 3m 보다 길게 늘리면 I2C 통신 오류가 날 수 있다',
      '접속부 방수 처리를 현장에서 다시 해야 한다'] },
  { key: 'shield', name: '복사 차폐(일사 차폐 덮개) 별도', rec: true, fit: '하우스 설치 전 필수',
    pros: ['햇빛을 받은 프로브는 실제보다 5~10℃ 높게 잰다 — 성적서 환경조건 데이터의 신뢰도를 지키는 가장 싼 장치',
      '농약·물방울이 프로브에 직접 닿는 것도 막는다'],
    cons: ['완제품에 포함되지 않는다 — 기상용 소형 차폐(여러 겹 원판형)를 따로 구매하거나 제작', '설치 높이·고정 방법을 하우스마다 맞춰야 한다'] },
];

const BUY_PLAN = [
  { key: 'p', stage: '파일럿', items: 'S31-CB 완제품 2대 + 복사 차폐 2개', why: '1NCE 로밍 접속·신호·배터리·다운링크를 실기기로 확인하는 것이 목적 — 분해·조합하지 않는다' },
  { key: 'm', stage: '본구축 (약 20대)', items: 'S31-CB 완제품 20대 + 복사 차폐 20개 + 예비 프로브 2개(10%)', why: '설치 위치를 하우스마다 따로 잡을 수 있어야 해서 외장 프로브형' },
  { key: 'r', stage: '운영 4년차~', items: '정품 배터리 팩 (전압 추세로 교체 시점 확인 후)', why: '미리 사 두지 않는다 — 수명이 신호 세기에 따라 4~8년(S31-CB, 8시간)으로 달라진다' },
  { key: 'c', stage: '매년', items: '기준 습도계 비교 → 벗어난 프로브 교체', why: '습도 센서 오염·드리프트 관리' },
];

const BUY_CHECK = [
  '판매처에 KC 인증번호와 LTE-M 지원 밴드를 함께 문의 (1NCE 구매 절차 ①)',
  '프로브가 커넥터식인지 케이블 일체형인지 — 프로브만 교체할 수 있는지',
  '교체용 배터리 팩 판매 여부·형식(ER26500 + SPC1520)·국내 배송 가능 여부',
  '모듈 변형(BG95 계열)과 펌웨어 버전 — 다운링크(원격 주기 변경) 지원 확인',
  '보증 기간과 불량 교환 절차 (해외 판매처면 반송 비용)',
];

const BuySection: React.FC = () => (
  <>
    <Alert type="success" showIcon style={{ marginBottom: 12 }}
      message={<span><b>권장</b> — <b>S31-CB 완제품(외장 프로브)</b> + <b>복사 차폐</b>를 함께 산다. 배터리·프로브는 파일럿 결과를 보고 예비품만 산다.</span>}
      description={<span style={{ fontSize: 12 }}>하우스에서는 '프로브는 작물 높이 그늘, 본체는 신호 좋은 높은 곳'이 핵심이라 외장 프로브형이 맞다.
        배터리를 일반 셀로 바꾸거나 다른 센서를 붙이는 조합은 통신 순간 전압 강하·펌웨어 호환 문제가 있어 파일럿 단계에서는 하지 않는다.</span>} />
    <Row gutter={12}>
      {BUY_OPTIONS.map(o => (
        <Col span={12} key={o.key}>
          <Card size="small" style={{ marginBottom: 12, borderTop: `3px solid ${o.rec ? '#1baf7a' : gray[300]}` }}
            title={<Space size={6} wrap><b style={{ fontSize: 13 }}>{o.name}</b>{o.rec && <RecTag />}<Tag style={{ margin: 0 }}>{o.fit}</Tag></Space>}>
            <div style={{ fontSize: 12.5, lineHeight: 1.75 }}>
              {o.pros.map(t => <div key={t} style={{ color: gray[700] }}><span style={{ color: '#15803d', fontWeight: 700 }}>＋</span> {t}</div>)}
              {o.cons.map(t => <div key={t} style={{ color: gray[600] }}><span style={{ color: '#b91c1c', fontWeight: 700 }}>－</span> {t}</div>)}
            </div>
          </Card>
        </Col>
      ))}
      <Col span={12}>
        <Card size="small" title="구매 전 판매처에 확인할 것" style={{ marginBottom: 12, borderTop: '3px solid #eda100' }}>
          <ol style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, lineHeight: 1.8, color: gray[700] }}>
            {BUY_CHECK.map(t => <li key={t}>{t}</li>)}
          </ol>
        </Card>
      </Col>
    </Row>
    <Card size="small" title="단계별 구매 계획">
      <Table size="small" pagination={false} rowKey="key" dataSource={BUY_PLAN} columns={[
        { title: '단계', dataIndex: 'stage', width: 130, render: (v: string) => <b style={cell}>{v}</b> },
        { title: '구매 품목', dataIndex: 'items', width: 330, render: (v: string) => <span style={cell}>{v}</span> },
        { title: '이유', dataIndex: 'why', render: (v: string) => <span style={{ ...cell, color: gray[600] }}>{v}</span> },
      ]} />
      <div style={{ fontSize: 11.5, color: gray[500], marginTop: 6 }}>가격은 판매처·환율에 따라 달라 적지 않았다. 견적을 받으면 이 표에 단가를 더해 예산을 잡는다.</div>
    </Card>
  </>
);

/* ── 절전 · 데이터 최적화 조치 ─────────────────────────────
   impact: 배터리·데이터에 대한 효과 크기(상/중/하). status: 적용(설정 가이드 AT 명령에 포함) / 파일럿 확인 / 운영 규칙 */
const SAVE_MEASURES = [
  { key: 'tdc', what: '업로드 1일 1회 (08:00)', how: 'AT+TDC=86400 · 08:00 무렵 ATZ · 장치 대장 원격 변경', batt: '최상', data: '상', status: '적용',
    why: '배터리는 통신 세션 횟수가 정한다. 1시간 업로드는 하루 24세션 — 수명 1.4년, 1일 1회는 계산 14.8년' },
  { key: 'n', what: '동봉 건수 32 (상한)', how: 'AT+CLOCKLOG=1,65535,60,32', batt: '하', data: '하', status: '적용',
    why: '원칙은 간격의 2배(48)지만 상한이 32 — 유실 1회는 재전송(REDPT)이 복구. 32건이어도 패킷 309B 로 가볍다' },
  { key: 'udp', what: 'UDP 사용 (MQTT·TLS 미사용)', how: 'AT+PRO=2,5', batt: '중', data: '상', status: '적용',
    why: '연결 수립·인증서 교환이 없어 세션당 왕복 1회. MQTTS 는 데이터 5.5배·약한 신호에서 배터리 1/3' },
  { key: 'alarm', what: '경보 업링크 끔 (서버에서 판정)', how: 'AT+SHTEMP=0,0 · AT+SHHUM=50,50', batt: '중', data: '중', status: '적용',
    why: '한낮 고온이면 매일 경보 세션이 추가된다. 임계 판정은 서버가 측정값으로 한다' },
  { key: 'ip', what: '서버 주소를 IP 로 설정', how: 'AT+SERVADDR=158.180.83.199,1999', batt: '하', data: '하', status: '적용',
    why: '도메인이면 매 세션 DNS 조회 왕복이 붙는다' },
  { key: 'redpt', what: '재전송 켬', how: 'AT+REDPT=1', batt: '하', data: '하', status: '적용',
    why: '유실 때만 동작. 끄면 결손이 복구되지 않아 현장 재방문 비용이 더 크다' },
  { key: 'signal', what: '설치 위치로 신호 확보 (-105dBm 초과)', how: '본체를 높은 곳 · 금속 뒤 피함 · 필요 시 LTE-M', batt: '최상', data: '중', status: '운영 규칙',
    why: '약한 신호에서 NB-IoT 는 같은 데이터를 수십~수백 번 반복 전송한다 — 어떤 설정보다 영향이 크다' },
  { key: 'gps', what: 'GPS(GNSS) 끔', how: '다운링크 0x11 00 또는 해당 AT 명령 (매뉴얼 확인)', batt: '상', data: '하', status: '파일럿 확인',
    why: '고정 설치라 위치가 필요 없다. 위치 측위는 수십 초 무선을 켜 세션보다 비쌀 수 있다. 기본값이 켜져 있는지 확인' },
  { key: 'int', what: '외부 인터럽트 끔', how: '인터럽트 모드 해제 (매뉴얼 확인)', batt: '중', data: '중', status: '파일럿 확인',
    why: '배선이 없는 입력 핀이 흔들리면 불필요한 업링크가 생긴다. 업링크 로그에 주기 밖 업로드가 보이면 의심' },
  { key: 'cb2', what: '대용량 배터리 모델 (S31-CB2)', how: '19,000mAh — 신호 약한 하우스 우선', batt: '최상', data: '-', status: '파일럿 확인',
    why: '같은 설정에서 수명 약 1.9배(1일 1회 약한 신호 8.7 → 16.0년). 공식 사양 · KC 모델명은 구매 전 확인' },
  { key: 'hex', what: 'HEX 페이로드 (선택)', how: 'AT+PRO=2,0 + 서버 HEX 해석 기능', batt: '하~중', data: '상', status: '파일럿 확인',
    why: '데이터 60~77% 감소. 신호 약한 하우스에서 배터리 +33%. 실기기 패킷으로 해석 기능을 만든 뒤 적용' },
  { key: 'test', what: '시험용 짧은 주기 되돌리기', how: '설치 시험 뒤 1시간 → 1일 1회 (장치 대장에서 확인)', batt: '상', data: '상', status: '운영 규칙',
    why: '시험 주기를 잊고 두면 한 달에 600KB 이상 — 장치 대장 최적화 배지가 "조치 필요" 로 알려준다' },
];
const IMPACT_COLOR: Record<string, string> = { '최상': '#7c2d12', '상': '#b45309', '중': '#0369a1', '하': gray[500], '하~중': '#0369a1', '-': gray[400] };
const STATUS_TAG: Record<string, string> = { '적용': 'green', '파일럿 확인': 'gold', '운영 규칙': 'blue' };

/* ── 극한 절감 (배터리 1순위 · 데이터 2순위) ──────────────────
   근거: Dragino 배터리 FAQ(S31-CB 측정값) · -CB 공통 설정 가이드 · S31-CB 매뉴얼 · 공식 디코더(2026-11-04 확인).
   수명 = 용량 ÷ (일 소모×365 + 자가방전 2%/년). 신호 약함 = 접속 3배 + 송신 바이트×8÷1kbps×200mA (추정) */
const DRAGINO_POWER = [
  { key: 'sleep', item: '대기 (세션 사이)', cur: '19.6µA', dur: '상시', e: '하루 0.47mAh', note: '세션 사이 LTE 모듈이 꺼져 있다 — 망 PSM 과 무관' },
  { key: 'samp', item: '업로드 시 측정', cur: '68.2mA', dur: '9.0초', e: '0.171mAh / 회', note: '업로드마다 1회 — 업로드 횟수에 비례' },
  { key: 'clk', item: 'CLOCKLOG 측정 (1시간)', cur: '8.5mA', dur: '0.13초', e: '0.0003mAh / 회', note: '하루 24회 = 0.007mAh — 1시간 측정은 거의 공짜' },
  { key: 'udp', item: '업링크 1회 · UDP', cur: '61.0mA', dur: '27.4초', e: '0.464mAh', note: '접속 · 송신 · 수신창 · 종료 포함. 가장 짧다', rec: true },
  { key: 'coap', item: '업링크 1회 · CoAP', cur: '64.0mA', dur: '35.7초', e: '0.634mAh', note: 'UDP +37%' },
  { key: 'mqtt', item: '업링크 1회 · MQTT', cur: '63.1mA', dur: '35.5초', e: '0.622mAh', note: 'UDP +34% — 문서: MQTT 는 UDP/CoAP 보다 전력 소모가 훨씬 크다' },
  { key: 'tcp', item: '업링크 1회 · TCP', cur: '63.4mA', dur: '38.1초', e: '0.672mAh', note: 'UDP +45%' },
];
/* UDP + HEX · 간격별 (8,500mAh = S31-CB / 19,000mAh = S31-CB2) */
const EXTREME_IV = [
  { key: '4', iv: '4시간 · N=8', pkt: 117, day: 4.29, cb: '4.9 (2.2)', cb2: '9.8 (4.6)', kb: 32.8, mb: 3.8, sleep: 11 },
  { key: '6', iv: '6시간 · N=12', pkt: 149, day: 3.02, cb: '6.7 (3.1)', cb2: '12.8 (6.4)', kb: 25.7, mb: 3.0, sleep: 16 },
  { key: '8', iv: '8시간 · N=16', pkt: 181, day: 2.38, cb: '8.2 (3.9)', cb2: '15.2 (8.0)', kb: 22.1, mb: 2.6, sleep: 20, tag: '확인 시각 우선' },
  { key: '12', iv: '12시간 · N=24', pkt: 245, day: 1.75, cb: '10.5 (5.4)', cb2: '18.7 (10.6)', kb: 18.5, mb: 2.2, sleep: 27 },
  { key: '24', iv: '24시간 · N=32', pkt: 309, day: 1.11, cb: '14.8 (8.7)', cb2: '24.2 (16.0)', kb: 11.2, mb: 1.3, sleep: 42, tag: '운영 적용' },
];
const EXTREME_CMDS: [string, string, string, string][] = [
  ['AT+PRO=2,0', 'UDP + HEX', '세션 최단(27초) · 패킷 -79%', '적용'],
  ['AT+IOTMOD=0', 'LTE-M 만 검색', 'NB-IoT · GSM 검색 시간 제거', '적용'],
  ['AT+SERVADDR=<IP>,1999', 'IP 직접 지정', '세션마다 DNS 왕복 제거', '적용'],
  ['AT+TDC=86400 · CLOCKLOG …,60,32', '세션 횟수 최소화 (1일 1회)', '배터리 1순위 변수. 동봉 상한 32건 + 재전송으로 유실 1회 복구', '적용'],
  ['AT+SHTEMP=0,0 · AT+SHHUM=50,50', '경보 업링크 끔', '임계 넘을 때마다 추가 세션 → 서버가 판정', '적용'],
  ['AT+GPS=0', 'GNSS 끔', '기본 꺼짐이지만 명시 — 측위는 수십 초 무선을 쓴다', '적용'],
  ['AT+INTMOD=0', '외부 인터럽트 끔', '배선 없는 입력의 헛 업링크 차단', '적용'],
  ['AT+REDPT=1', '재전송 켬', '유실 때만 동작 — 끄면 결손 복구 비용(현장 방문)이 더 크다', '적용'],
  ['AT+QBAND=<KT LTE-M 밴드>,0', '밴드 고정', '모든 밴드 검색을 줄여 첫 접속 · 재접속 단축. 밴드는 파일럿에서 확인 후', '파일럿'],
  ['AT+CSQTIME=<분>', '망 검색 상한', '신호 없을 때 헛검색 상한. 파일럿 접속 시간의 2~3배로', '파일럿'],
];
const EXTREME_NOT: [string, string][] = [
  ['MQTT · MQTTS · TCP', '세션이 34~45% 길고 데이터 3~19배. 인증이 필요해질 때만'],
  ['재전송 끄기 (REDPT=0)', '유실이 없으면 비용 0 이라 끌 이유가 없다'],
  ['1시간마다 업로드', '측정과 업로드를 같게 하면 1.4년 (8,500mAh)'],
  ['N 을 간격의 2배보다 크게', '배터리 영향은 거의 없지만 데이터만 는다'],
  ['24시간 간격', '배터리는 가장 길지만 N 상한 32 로 유실 1회에 16시간 결손 — 성적서 원자료에 공백'],
  ['PSM · eDRX 협상', '-CB 는 세션 사이 모듈을 끄므로(대기 19.6µA) 망 PSM 이 수명을 좌우하지 않는다'],
];
const HEX_PILOT: [string, string][] = [
  ['전송 형태', '바이너리인지 16진 문자열인지 — 서버는 둘 다 받는다. 업링크 로그 원문 길이로 확인(N=32 면 309B / 618자)'],
  ['ACK · 재전송', "HEX 모드에서도 서버 ACK('Confirm=200')로 REDPT 가 동작하는지 — ACK 유실 시 다음 업로드에 이전 패킷이 붙어 오는지"],
  ['원격 주기 변경', "HEX 다운링크(01 TDC · 08 CLOCKLOG, 명령마다 한 패킷) 반영. 확인 응답(Downklink_Ack)은 JSON 전용이라 '업로드 간격 관측'으로 판정한다"],
  ['시각', '항목 시각이 Unix epoch(UTC) 로 맞는지 — 업로드 시각과 서버 수신 시각 차이 확인'],
  ['되돌리기', '문제가 있으면 AT+PRO=2,5 로 JSON 복귀. 서버는 두 형식을 동시에 받으므로 장치별로 섞여도 된다'],
];

const ExtremeSection: React.FC = () => (
  <>
    <Alert type="success" showIcon style={{ marginBottom: 12 }}
      message={<span><b>극한 절감 구성</b> — <b>UDP + HEX(바이너리)</b> · LTE-M 고정 · IP 직접 · 경보 · GPS · 인터럽트 끔 · 재전송 켬 · <b>세션 횟수 최소화</b></span>}
      description={<span style={{ fontSize: 12 }}>Dragino 실측으로 보면 업링크 1회(UDP)는 <b>27.4초</b>이고 그중 데이터 송신은 1초도 안 된다 — 배터리는 <b>패킷 크기가 아니라 세션 횟수 × 방식</b>이 정한다.
        그래서 배터리 1순위는 UDP · 간격, 데이터 2순위는 HEX(-79%)다. 적용한 1일 1회 · N=32 면 S31-CB 계산 14.8년(전지 수명이 먼저 한계) · 약한 신호 8.7년, 데이터 10년 <b>1.3MB(500MB 의 0.26%)</b>.
        서버는 HEX 를 해석하므로(2026-11-04) 권장 명령이 <Text code>AT+PRO=2,0</Text> 으로 바뀌었다.</span>} />
    <Row gutter={12} style={{ overflowX: 'hidden' }}>
      <Col span={12}>
        <Card size="small" title="Dragino 공식 측정값 — S31-CB" extra={<Basis kind="실측" />} style={{ marginBottom: 12 }}>
          <Table size="small" pagination={false} rowKey="key" dataSource={DRAGINO_POWER} columns={[
            { title: '항목', dataIndex: 'item', width: 150, render: (v: string, r: any) => <Space size={4}><b style={cell}>{v}</b>{r.rec && <RecTag />}</Space> },
            { title: '전류', dataIndex: 'cur', width: 64, render: (v: string) => <span style={num}>{v}</span> },
            { title: '시간', dataIndex: 'dur', width: 56, render: (v: string) => <span style={num}>{v}</span> },
            { title: '에너지', dataIndex: 'e', width: 104, render: (v: string) => <b style={num}>{v}</b> },
            { title: '의미', dataIndex: 'note', render: (v: string) => <span style={{ ...cell, color: gray[600] }}>{v}</span> },
          ]} />
          <div style={{ fontSize: 11.5, color: gray[500], marginTop: 6 }}>출처: Dragino 배터리 FAQ(Battery Info &amp; Power Consumption Analyze)의 S31-CB 입력값. 실험실(신호 양호) 측정이다.</div>
        </Card>
        <Card size="small" title="S31-CB2 — 배터리 19,000mAh" extra={<Basis kind="계산" />} style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 12.5, lineHeight: 1.85, color: gray[700] }}>
            S31-CB(8,500mAh)보다 용량이 <b>2.2배</b>다. 같은 통신 모듈 · 펌웨어라고 보면 세션 전력은 같고 수명만 용량에 비례한다(자가방전 2%/년도 같이 커진다).
            <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
              <li><b>신호 약한 하우스에서 차이가 크다</b> — 1일 1회에서 8.7년 → <b>16.0년</b>(8시간이면 3.9 → 8.0년). 설치 위치 조정으로도 신호를 못 올리는 하우스에 우선 배치한다.</li>
              <li>신호 양호면 1일 1회 S31-CB 도 14.8년 계산이라 <b>전지 자체 수명 · 1NCE 10년</b>이 먼저 한계다 — 신호 양호한 하우스에는 이득이 없다.</li>
              <li>남는 배터리를 <b>간격 단축</b>에 쓸 수 있다: 6시간 12.8년 · 4시간 9.8년 (데이터는 10년 3~4MB 로 여전히 0.8%).</li>
              <li>배터리 교체 주기가 사실상 사라져 <b>20대 현장 재방문 비용</b>이 준다. 대신 단가 · 크기 · 무게가 늘어 설치 고정 방법을 다시 본다.</li>
              <li>확인할 것: Dragino 공식 데이터시트(위키에 없음 — 판매처 목록 기준), KC 인증 모델명, 1NCE '-1T' 구성 여부, 같은 펌웨어(HEX 배치) 인지 — 모델 바이트가 다르면 서버가 '거절 로그'에 남긴다.</li>
            </ul>
          </div>
        </Card>
      </Col>
      <Col span={12}>
        <Card size="small" title="UDP + HEX · 간격별 수명과 데이터" extra={<Basis kind="계산" />} style={{ marginBottom: 12 }}>
          <Table size="small" pagination={false} rowKey="key" dataSource={EXTREME_IV} columns={[
            { title: '간격', dataIndex: 'iv', width: 110, render: (v: string, r: any) => <div><b style={cell}>{v}</b>{r.tag && <div><Tag color={r.tag === '운영 권장' ? 'green' : r.tag === '극한 절감' ? 'blue' : 'orange'} style={{ margin: 0, fontSize: 11 }}>{r.tag}</Tag></div>}</div> },
            { title: '패킷', dataIndex: 'pkt', width: 52, render: (v: number) => <span style={num}>{v}B</span> },
            { title: '일 소모', dataIndex: 'day', width: 70, render: (v: number) => <span style={num}>{v}mAh</span> },
            { title: 'CB 수명(약함)', dataIndex: 'cb', width: 92, render: (v: string) => <b style={num}>{v}년</b> },
            { title: 'CB2 수명(약함)', dataIndex: 'cb2', width: 98, render: (v: string) => <b style={num}>{v}년</b> },
            { title: '월 · 10년', key: 'd', width: 90, render: (_: any, r: any) => <span style={num}>{r.kb}KB · {r.mb}MB</span> },
            { title: '대기 비중', dataIndex: 'sleep', width: 64, render: (v: number) => <span style={num}>{v}%</span> },
          ]} />
          <div style={{ fontSize: 11.5, color: gray[500], marginTop: 6, lineHeight: 1.7 }}>
            괄호는 신호 약함(-105dBm 이하) 추정. 간격을 늘릴수록 대기 전류(하루 0.47mAh)가 바닥이 된다 — 24시간이면 소모의 42%.
            적용은 <b>1일 1회 · N=32</b> — 동봉만으로 유실을 자가 복구하는 가장 긴 간격은 12시간(N=24)이지만, 1일 1회는 재전송(REDPT)이 그 역할을 하고 세션이 절반이라 수명이 더 길다.
          </div>
        </Card>
        <Card size="small" title="데이터 타입 × 통신 방식 — 8시간 · N=16" extra={<Basis kind="추정" />} style={{ marginBottom: 12 }}>
          <Table size="small" pagination={false} rowKey="key" dataSource={COMBOS} columns={[
            { title: '조합', dataIndex: 'combo', width: 120, render: (v: string, r: any) => <Space size={4}><b style={cell}>{v}</b>{r.rec && <RecTag />}</Space> },
            { title: '세션 바이트', dataIndex: 'bytes', width: 70, render: (v: number) => <span style={num}>{v.toLocaleString('en-US')}B</span> },
            { title: '월', dataIndex: 'kb', width: 64, render: (v: number) => <span style={num}>{v}KB</span> },
            { title: '양호 (세션·CB 수명)', dataIndex: 'good', width: 116, render: (v: string) => <span style={num}>{v}</span> },
            { title: '약함', dataIndex: 'weak', width: 104, render: (v: string) => <span style={num}>{v}</span> },
            { title: '판단', dataIndex: 'note', render: (v: string) => <span style={{ ...cell, color: gray[600] }}>{v}</span> },
          ]} />
        </Card>
      </Col>
    </Row>
    <Row gutter={12} style={{ overflowX: 'hidden' }}>
      <Col span={12}>
        <Card size="small" title="극한 절감 명령 세트 (영향 큰 순)" style={{ marginBottom: 12 }}>
          <Table size="small" pagination={false} rowKey={(r: any) => r[0]} dataSource={EXTREME_CMDS} columns={[
            { title: '명령', key: 'c', width: 190, render: (_: any, r: any) => <span style={{ ...cell, fontFamily: font.mono, fontSize: 11 }}>{r[0]}</span> },
            { title: '조치', key: 'w', width: 110, render: (_: any, r: any) => <b style={cell}>{r[1]}</b> },
            { title: '효과', key: 'e', render: (_: any, r: any) => <span style={{ ...cell, color: gray[600] }}>{r[2]}</span> },
            { title: '상태', key: 's', width: 64, render: (_: any, r: any) => <Tag color={r[3] === '적용' ? 'green' : 'gold'} style={{ margin: 0 }}>{r[3]}</Tag> },
          ]} />
          <div style={{ fontSize: 11.5, color: gray[500], marginTop: 6 }}>'적용' 은 서버 권장 명령(AT 명령 · 블루투스 카테고리의 복사 버튼)에 들어 있다.</div>
        </Card>
      </Col>
      <Col span={12}>
        <Card size="small" title="효과가 없거나 하지 말 것" style={{ marginBottom: 12 }}>
          <Table size="small" pagination={false} showHeader={false} rowKey={(r: any) => r[0]} dataSource={EXTREME_NOT} columns={[
            { key: 'k', width: 170, render: (_: any, r: any) => <b style={cell}>{r[0]}</b> },
            { key: 'v', render: (_: any, r: any) => <span style={{ ...cell, color: gray[600] }}>{r[1]}</span> },
          ]} />
        </Card>
        <Card size="small" title="HEX 적용 — 파일럿 첫 장치에서 확인할 것" style={{ marginBottom: 12 }}>
          <Table size="small" pagination={false} showHeader={false} rowKey={(r: any) => r[0]} dataSource={HEX_PILOT} columns={[
            { key: 'k', width: 110, render: (_: any, r: any) => <b style={cell}>{r[0]}</b> },
            { key: 'v', render: (_: any, r: any) => <span style={{ ...cell, color: gray[600] }}>{r[1]}</span> },
          ]} />
        </Card>
      </Col>
    </Row>
  </>
);

const SaveSection: React.FC = () => (
  <>
    <Alert type="info" showIcon style={{ marginBottom: 12 }}
      message={<span><b>장치 대장 '최적화' 배지</b>가 이 기준을 장치마다 자동으로 판정한다 — <b>최적 / 개선 권장 / 조치 필요</b>.</span>}
      description={<span style={{ fontSize: 12 }}>판정 항목: 업로드 간격(절전) · 동봉 건수(결손·낭비) · 월 데이터(최근 30일 실측 우선, 없으면 설정값 추정) · 신호 · 배터리.
        배지에 마우스를 올리면 항목별 근거가, 설정으로 고칠 수 있는 문제면 배지를 누르면 권장값이 채워진 주기 변경 창이 열린다.</span>} />
    <Card size="small" title="배터리 절약 · 통신 데이터 최적화 조치 (효과 큰 순 정렬 가능)">
      <Table size="small" pagination={false} rowKey="key" dataSource={SAVE_MEASURES} tableLayout="fixed" columns={[
        { title: '조치', dataIndex: 'what', width: 200, render: (v: string) => <b style={cell}>{v}</b> },
        { title: '방법', dataIndex: 'how', width: 220, render: (v: string) => <span style={{ ...cell, fontFamily: font.mono, fontSize: 11 }}>{v}</span> },
        { title: '배터리', dataIndex: 'batt', width: 64, sorter: (a: any, b: any) => ['최상', '상', '하~중', '중', '하', '-'].indexOf(a.batt) - ['최상', '상', '하~중', '중', '하', '-'].indexOf(b.batt),
          render: (v: string) => <b style={{ ...cell, color: IMPACT_COLOR[v] }}>{v}</b> },
        { title: '데이터', dataIndex: 'data', width: 64, render: (v: string) => <b style={{ ...cell, color: IMPACT_COLOR[v] }}>{v}</b> },
        { title: '상태', dataIndex: 'status', width: 90, render: (v: string) => <Tag color={STATUS_TAG[v]} style={{ margin: 0 }}>{v}</Tag> },
        { title: '이유', dataIndex: 'why', render: (v: string) => <span style={{ ...cell, color: gray[600] }}>{v}</span> },
      ]} />
      <div style={{ fontSize: 11.5, color: gray[500], marginTop: 8, lineHeight: 1.7 }}>
        상태 — <Tag color="green" style={{ margin: 0 }}>적용</Tag> 설정 가이드 AT 명령에 이미 들어 있음 ·
        <Tag color="gold" style={{ margin: '0 4px' }}>파일럿 확인</Tag> 명령 이름·기본값·망 지원을 실기기·통신사로 확인 후 적용 ·
        <Tag color="blue" style={{ margin: 0 }}>운영 규칙</Tag> 설치·운영 때 사람이 지킬 것
      </div>
    </Card>
  </>
);

/* ── 전문가 검토: 주의사항 · 고려사항 ─────────────────────── */
const EXPERT = [
  { area: '측정 정확도', color: '#b91c1c', items: [
    ['일사 차폐 없이 설치', '햇빛을 받은 프로브는 실제보다 5~10℃ 높게 잰다. 차폐 덮개 없이 얻은 한낮 값은 성적서 근거로 쓸 수 없다'],
    ['결로 · 포화 습도', '새벽 결로 뒤 습도가 99.9% 에 붙어 몇 시간 내려오지 않을 수 있다(센서 표면 수분). 이 구간은 측정 한계로 주석 처리'],
    ['측정 높이 · 위치', '작물 생장점 높이, 하우스 중앙부. 출입구·환기창·난방기 바로 옆은 국소값이라 대표성이 없다'],
    ['정확도 표기', 'SHT31 온도 ±0.2℃ · 습도 ±2%RH(제조사 사양). 성적서에는 장비 사양과 교정 이력을 함께 적는다'],
  ] },
  { area: '교정 · 추적성 (GLP)', color: '#6b21a8', items: [
    ['기준기 비교', '설치 전·연 1회 교정된 기준 온습도계와 같은 장소에서 비교해 편차를 기록한다(장치 대장 비고 또는 별도 교정 대장)'],
    ['원자료 불변', '측정값은 수정하지 않는다. 이상값은 지우지 않고 주석으로 남긴다 — 서버도 시각 의심 값을 버리지 않고 표시만 한다'],
    ['장치 ↔ 시험 연결', '어느 기간에 어느 장치가 어느 시험을 쟀는지는 시험 배정(기간)으로 남긴다. 장치를 옮기면 배정을 먼저 종료'],
    ['시각 기준', '저장은 UTC, 화면·엑셀은 KST. 단말 시각 기준(UTC/KST)은 파일럿에서 확정 — 9시간 어긋남은 가장 흔한 오류'],
  ] },
  { area: '데이터 무결성', color: '#0369a1', items: [
    ['결손 감시', "'값이 나쁘다'보다 '값이 안 온다'가 더 위험하다(TAPO 11일 중단 사고). 수신 지연 경보를 끄지 않는다"],
    ['고착 · 튐 값', '같은 값이 몇 시간 그대로거나 한 시간에 10℃ 이상 튀면 센서·배선 이상을 의심한다'],
    ['중복 · 재전송', '동봉·재전송으로 같은 측정이 여러 번 온다 — 서버가 (장치, 시각) 기준으로 1건만 저장한다'],
    ['시계 밀림', '단말 시계는 조금씩 밀린다. 업로드 시각이 30분 이상 밀리면 재정렬(ATZ), 3일 이상 어긋난 값은 시각 의심 표시'],
  ] },
  { area: '배터리 · 전원', color: '#b45309', items: [
    ['저온', 'Li/SOCl2 는 영하에서 용량·순간 전류가 준다. 겨울 하우스 야간 영하가 길면 통신 순간 재부팅이 생길 수 있다'],
    ['장기 보관 후 첫 사용', '오래 보관한 리튬 1차전지는 표면 피막으로 첫 통신 때 전압이 순간 떨어질 수 있다 — 설치 전 사무실에서 1~2회 업로드로 깨운다'],
    ['전압 해석', '3.6V 근처를 오래 유지하다 끝에서 급히 떨어진다. 3.3V 아래로 내려가면 남은 기간이 짧다는 뜻이다'],
    ['교체 품질', '정품 팩(셀 + 보조 커패시터) 그대로 교체. 일반 셀 단독 교체는 통신 실패의 흔한 원인'],
  ] },
  { area: '통신 · 망', color: '#15803d', items: [
    ['하우스 구조', '철골·보온 커튼·물기 있는 비닐은 전파를 약하게 한다. 커튼을 닫은 겨울 야간에 신호가 가장 나쁘다 — 그 시간대 업링크 신호로 판단'],
    ['로밍 · 망 장애', 'KT 무선망 · 1NCE 코어(유럽) 어느 쪽 장애에도 업로드가 빠질 수 있다. 동봉 2배 + 재전송이 이를 복구한다'],
    ['SIM 관리', 'ICCID·IMEI·활성일을 장치 대장에 기록. 10년 만료일 · 잔량을 1NCE 포털로 연 1회 점검'],
    ['데이터 한도', '최적화 배지와 1NCE 포털 사용량을 월 1회 대조한다'],
  ] },
  { area: '보안', color: '#334155', items: [
    ['UDP 무인증', '누구나 IMEI 를 흉내 내 보낼 수 있다. 서버는 등록 IMEI·값 범위·시각을 검증하고 거절 이력을 남긴다 — 거절 로그를 주기적으로 본다'],
    ['IMEI 노출', 'IMEI 는 장치 라벨에 있다. 장치 사진·라벨을 외부 공유하지 않는다'],
    ['강화 선택지', '요구가 생기면 MQTTS(인증서) 로 전환 가능 — 대신 데이터 5.5배·배터리 감소를 감수'],
  ] },
  { area: '설치 · 운영', color: '#0f766e', items: [
    ['물리 손상', '방제기·작업 동선·설치류가 케이블을 끊는다. 케이블은 철골을 따라 고정하고 바닥에 늘어뜨리지 않는다'],
    ['방제 · 세척', '농약·세척수가 본체·프로브에 직접 닿지 않게 한다. 방수 패킹은 설치 때마다 확인'],
    ['라벨 · 예비품', '본체에 장치명·하우스 코드 라벨. 예비 단말 1~2대와 예비 프로브를 두면 고장 시 결손 기간이 줄어든다'],
    ['월간 점검', '최적화 배지(조치 필요 0) · 배터리 등급 · 신호 등급 · 수신 지연 이력 · 거절 로그'],
  ] },
  { area: '법규 · 인증', color: '#9f1239', items: [
    ['KC 전파인증', '국내에서 무선기기를 쓰려면 KC 인증이 필요하다. 해외 직구 단말은 인증·개통 가능 여부를 구매 전에 확인'],
    ['리튬전지 운송', '교체 배터리 해외 주문은 항공 운송 제한으로 늦어질 수 있다 — 국내 재고 확인'],
  ] },
];

const ExpertSection: React.FC = () => (
  <>
    <Alert type="warning" showIcon style={{ marginBottom: 12 }}
      message={<span><b>전문가 관점 핵심 3가지</b> — ① 일사 차폐 없는 값은 성적서 근거가 못 된다 ② 신호 세기가 배터리 수명을 가장 크게 좌우한다 ③ '값이 안 오는 것'을 감시해야 한다</span>}
      description={<span style={{ fontSize: 12 }}>아래는 하우스 온습도 기록을 시험 성적서의 환경조건 근거로 쓰는 관점에서 정리한 주의사항이다. 설치·운영 체크리스트로 그대로 쓴다.</span>} />
    <Row gutter={12}>
      {EXPERT.map(sec => (
        <Col span={12} key={sec.area}>
          <Card size="small" style={{ marginBottom: 12, borderLeft: `3px solid ${sec.color}` }}
            title={<b style={{ fontSize: 13, color: sec.color }}>{sec.area}</b>}>
            {sec.items.map(([t, d]) => (
              <div key={t} style={{ fontSize: 12.5, lineHeight: 1.7, marginBottom: 4 }}>
                <b style={{ color: gray[800] }}>{t}</b> <span style={{ color: gray[600] }}>— {d}</span>
              </div>
            ))}
          </Card>
        </Col>
      ))}
    </Row>
  </>
);

const PlanSection: React.FC = () => (
  <>
    <Alert type="success" showIcon style={{ marginBottom: 12 }}
      message={<span><b>적용 요금제 — 1NCE 10년 · 500MB</b> (SIM 1장 21,000원 VAT 별도 · 배송비 5,000원 · MDS테크놀로지, 월 요금 없음)</span>}
      description={<span style={{ fontSize: 12 }}>적용 구성(1일 1회 · N=32 · UDP+HEX)은 10년 약 <b>1.3MB</b> 로 500MB 의 0.26%다. 국내 통신사(KT · LGU+ · SKT) 요금제는 쓰지 않는다.
        한국 커버리지(LTE-M 로밍)는 구매 전 1NCE 서면 확인이 조건이다.</span>} />
    <Row gutter={12}>
      <Col span={11}>
        <Card size="small" title="1NCE IoT Lifetime Flat" extra={<Basis kind="공개요금" />} style={{ marginBottom: 12 }}>
          <Table size="small" pagination={false} showHeader={false} rowKey={(r: any) => r[0]} dataSource={ONCE_PLAN} columns={[
            { key: 'k', width: 90, render: (_: any, r: any) => <b style={cell}>{r[0]}</b> },
            { key: 'v', render: (_: any, r: any) => <span style={{ ...cell, color: gray[700] }}>{r[1]}</span> },
          ]} />
        </Card>
        <Card size="small" title="구축 규모별 통신비 (1회 구매)" extra={<Tag style={{ margin: 0, fontSize: 11 }} color="purple">구매가</Tag>} style={{ marginBottom: 12 }}>
          <Table size="small" pagination={false} rowKey="key" dataSource={ONCE_FLEET} columns={[
            { title: '수량', dataIndex: 'n', render: (v: string) => <span style={cell}>{v}</span> },
            { title: 'SIM', dataIndex: 'sim', align: 'right' as const, render: (v: number) => <span style={num}>{won(v)}</span> },
            { title: '배송', key: 'ship', align: 'right' as const, render: () => <span style={num}>{won(SHIP_FEE)}</span> },
            { title: '합계 (VAT 별도)', dataIndex: 'total', align: 'right' as const, render: (v: number) => <b style={num}>{won(v)}</b> },
          ]} />
          <div style={{ fontSize: 11.5, color: gray[500], marginTop: 6, lineHeight: 1.7 }}>
            MDS테크놀로지 단가 21,000원(VAT 별도) · 배송비 5,000원/주문. VAT 포함 SIM 1장 23,100원. 10년으로 나누면 1대 연 2,100원.
            단말 배터리(S31-CB 1일 1회 계산 14.8년 · 약한 신호 8.7년)는 필요하면 교체하고 SIM 은 그대로 10년 쓴다.
          </div>
        </Card>
      </Col>
      <Col span={13}>
        <Card size="small" title="업로드 주기별 10년 사용량 (31일 실측 × 120개월)" extra={<Basis kind="실측" />} style={{ marginBottom: 12 }}>
          <Table size="small" pagination={false} rowKey="key" dataSource={MEASURED} columns={[
            { title: '설정', dataIndex: 'cfg', width: 120, render: (v: string, r: any) => <Space size={4}><span style={cell}>{v}</span>{r.rec && <RecTag />}</Space> },
            { title: '월 데이터', dataIndex: 'kb', width: 80, render: (v: number) => <span style={num}>{v}KB</span> },
            { title: '10년 누적 / 500MB', key: 'u', width: 210, render: (_: any, r: any) => <Usage kb={r.kb} /> },
            { title: '10년 뒤 잔량', key: 'left', render: (_: any, r: any) => <span style={num}>{(ONCE_MB - tenYearMb(r.kb)).toFixed(0)}MB</span> },
          ]} />
        </Card>
        <Card size="small" title="500MB 를 넘길 수 있는 경우" style={{ marginBottom: 12 }}>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, lineHeight: 1.85, color: gray[700] }}>
            <li>MQTTS · 12시간 업로드: 월 422.6KB → 10년 약 50MB (10%) — 데이터는 괜찮고 배터리가 절반.</li>
            <li>1시간마다 업로드(측정 = 업로드): 10년 약 35MB (7%) — 데이터보다 배터리(4~5개월)가 먼저 끝난다.</li>
            <li>단말 고장으로 재부팅 · 재전송이 반복되는 경우 — <b>포털 사용량 경보</b>로 잡는다.</li>
            <li style={{ color: gray[500] }}>잔량 · 개통 상태는 1NCE 홈페이지에서 확인한다. 모자라면 데이터 충전, 10년이 끝나면 연장 — 조건은 MDS테크놀로지에 확인한다.</li>
          </ul>
        </Card>
      </Col>
    </Row>
  </>
);

const VolumeSection: React.FC = () => (
  <>
    <Card size="small" title="1시간 측정 · 업로드 주기별 월 데이터량 (31일 실측)" style={{ marginBottom: 12 }}
      extra={<Basis kind="실측" />}>
      <Table size="small" pagination={false} rowKey="key" dataSource={MEASURED} scroll={{ x: 'max-content' }} columns={[
        { title: '설정', dataIndex: 'cfg', width: 120, render: (v: string, r: any) => <Space size={4}><b style={cell}>{v}</b>{r.rec && <RecTag />}</Space> },
        { title: 'AT 설정', dataIndex: 'at', width: 200, render: (v: string) => <Text code style={{ fontSize: 11 }}>{v}</Text> },
        { title: '업로드/월', dataIndex: 'up', width: 80, render: (v: number) => <span style={num}>{v}회</span> },
        { title: '패킷 크기', dataIndex: 'pkt', width: 80, render: (v: number) => <span style={num}>{v.toLocaleString('en-US')}B</span> },
        { title: '송신', dataIndex: 'ul', width: 70, render: (v: number) => <span style={num}>{v}KB</span> },
        { title: 'ACK 수신', dataIndex: 'dl', width: 76, render: (v: number) => <span style={num}>{v}KB</span> },
        { title: '합계', dataIndex: 'kb', width: 76, render: (v: number) => <b style={num}>{v}KB</b> },
        { title: '결손', dataIndex: 'gap', width: 56, render: (v: number) => <span style={num}>{v}h</span> },
        { title: '결손 위험', dataIndex: 'risk', render: (v: string) => <span style={{ ...cell, color: gray[600] }}>{v}</span> },
      ]} />
      <div style={{ fontSize: 11.5, color: gray[500], marginTop: 8, lineHeight: 1.7 }}>
        측정 방법: 시뮬레이터가 1시간마다 더미 온습도를 기록하고 업로드 주기마다 최근 N건을 JSON 으로 묶어 실제 수신기에 보냈다(장치 1대, 31일, 744시간).
        통신량 = 페이로드 + IPv4/UDP 헤더 28B, 서버 ACK = 11B + 28B. 744개 정시 측정이 모두 저장돼 결손 0.
        N(동봉 건수)은 업로드 주기 분량보다 커야 유실을 복구한다 — 12시간이면 N=24, 24시간이면 N=32(상한)가 안전하다.
      </div>
    </Card>
    <Row gutter={12}>
      <Col span={14}>
        <Card size="small" title="통신 장애가 있던 달 (31일 실측)" style={{ marginBottom: 12 }} extra={<Basis kind="실측" />}>
          <Table size="small" pagination={false} rowKey="key" dataSource={MEASURED_BAD} columns={[
            { title: '상황', dataIndex: 'cfg', render: (v: string) => <span style={cell}>{v}</span> },
            { title: '송신', dataIndex: 'up', width: 60, render: (v: number) => <span style={num}>{v}회</span> },
            { title: '월 데이터', dataIndex: 'kb', width: 80, render: (v: number) => <b style={num}>{v}KB</b> },
            { title: '결손', dataIndex: 'gap', width: 50, render: (v: number) => <span style={num}>{v}h</span> },
            { title: '비고', dataIndex: 'note', render: (v: string) => <span style={{ ...cell, color: gray[600] }}>{v}</span> },
          ]} />
        </Card>
      </Col>
      <Col span={10}>
        <Card size="small" title="추가 세션 · 데이터 여유" style={{ marginBottom: 12 }}>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, lineHeight: 1.85, color: gray[700] }}>
            <li>업로드 1회 = 0.9~1.2KB. 1NCE 10년 500MB 는 권장 구성(월 85.6KB) 10년치의 약 50배라 데이터 여유는 충분하다 — 아낄 것은 <b>배터리(세션 수)</b>다.</li>
            <li>설치 때 버튼 업로드, 재부팅, 경보 업링크(SHTEMP/SHHUM)가 추가 세션이 된다 → <b>경보는 끄고 서버에서 판정</b>한다.</li>
            <li>서버 주소를 도메인으로 넣으면 DNS 조회가 매번 붙는다 → <b>IP 로 설정</b>(현재 설정 가이드 값).</li>
            <li>업로드 주기를 6시간(1일 4회)으로 줄여도 10년 약 11~18MB 로 데이터는 문제없고, 배터리가 약 25% 더 든다(추정).</li>
          </ul>
        </Card>
      </Col>
    </Row>
  </>
);

const PayloadSection: React.FC = () => (
  <Card size="small" title="데이터 타입(페이로드) 비교 — UDP 기준">
    <Table size="small" pagination={false} rowKey="key" dataSource={PAYLOADS} scroll={{ x: 'max-content' }} columns={[
      { title: '형식', dataIndex: 'name', width: 160, render: (v: string, r: any) => <div><Space size={4}><b style={cell}>{v}</b>{r.rec && <RecTag />}</Space><div><Text code style={{ fontSize: 11 }}>{r.at}</Text></div></div> },
      { title: '패킷 (N=24)', dataIndex: 'n24', width: 90, render: (v: string) => <span style={num}>{v}</span> },
      { title: '월 · 12시간', dataIndex: 'm12', width: 90, render: (v: number) => <b style={num}>{v}KB</b> },
      { title: '월 · 24시간', dataIndex: 'm24', width: 90, render: (v: number) => <span style={num}>{v}KB</span> },
      { title: '근거', dataIndex: 'basis', width: 60, render: (v: any) => <Basis kind={v} /> },
      { title: '서버 디코더', dataIndex: 'decoder', width: 100, render: (v: string) => <span style={cell}>{v}</span> },
      { title: '장단점', key: 'pc', render: (_: any, r: any) => <div style={cell}><div>＋ {r.pros}</div><div style={{ color: gray[500] }}>－ {r.cons}</div></div> },
    ]} />
    <div style={{ fontSize: 12.5, lineHeight: 1.85, color: gray[700], marginTop: 10 }}>
      <b>판단</b>: <b>HEX(바이너리) 적용</b>. 요금은 10년 정액이라 같지만 데이터 -79% 이고, 약한 신호에서는 송신 시간이 줄어 배터리 +15% 다(신호 양호면 같다 — 세션 27초 중 송신은 1초 미만).
      서버가 Dragino 공식 디코더와 같은 배치로 해석하고, 매뉴얼 예시 패킷(117B)으로 검증했다. 업링크 로그에는 16진 원문이, 측정 데이터에는 해석값이 남는다.
      <div style={{ fontSize: 11.5, color: gray[500], marginTop: 4 }}>HEX 배치: 헤더 53B(IMEI 8 · IMSI 8 · 버전 2 · 배터리 2 · 신호 1 · MOD 1 · DS18B20 2 · PA4 1 · 인터럽트 2 · ADC 2 · count 4 · 온도 2 · 습도 2 · 시각 4 · 위도 4 · 경도 4 · GPS 시각 4) + 항목당 8B(온도 2 · 습도 2 · 시각 4).</div>
    </div>
  </Card>
);

const ProtocolSection: React.FC = () => (
  <Card size="small" title="통신 방식 비교 — HEX · 1시간 측정 · N=24 · 세션 전력은 Dragino 실측">
    <Table size="small" pagination={false} rowKey="key" dataSource={PROTOCOLS} scroll={{ x: 'max-content' }} columns={[
      { title: '방식', dataIndex: 'name', width: 80, render: (v: string, r: any) => <Space size={4}><b style={cell}>{v}</b>{r.rec && <RecTag />}</Space> },
      { title: 'AT 설정', dataIndex: 'at', width: 180, render: (v: string) => <Text code style={{ fontSize: 11 }}>{v}</Text> },
      { title: '세션 구성', dataIndex: 'session', width: 200, render: (v: string) => <span style={cell}>{v}</span> },
      { title: '세션당 추가', dataIndex: 'ovh', width: 100, render: (v: string) => <span style={num}>{v}</span> },
      { title: '월 · 12시간 (10년 누적)', key: 'm12', width: 200, render: (_: any, r: any) => <div><b style={num}>{r.m12}KB</b><Usage kb={r.m12} /></div> },
      { title: '월 · 24시간', dataIndex: 'm24', width: 86, render: (v: number) => <span style={num}>{v}KB</span> },
      { title: '배터리', dataIndex: 'battery', width: 100, render: (v: string) => <span style={cell}>{v}</span> },
      { title: '근거', dataIndex: 'basis', width: 60, render: (v: any) => <Basis kind={v} /> },
      { title: '서버', dataIndex: 'server', width: 160, render: (v: string) => <span style={cell}>{v}</span> },
      { title: '특징', dataIndex: 'note', width: 320, render: (v: string) => <span style={{ ...cell, color: gray[600] }}>{v}</span> },
    ]} />
    <div style={{ fontSize: 12.5, lineHeight: 1.85, color: gray[700], marginTop: 10 }}>
      <b>판단</b>: UDP 가 데이터·배터리 모두 최소이고 수신 서버가 이미 가동 중이다. UDP 에는 인증이 없지만 서버가 <b>등록된 IMEI 만 저장</b>하고
      값 범위·시각을 검증하며 거절 이력을 남긴다. 보안 요구가 생기면 AT 명령만 바꿔 MQTTS 로 옮길 수 있다(10년 약 50MB 로 500MB 안 · 요금 그대로, 배터리 절반).
      <div style={{ fontSize: 11.5, color: gray[500], marginTop: 4 }}>
        세션당 추가 바이트: TCP 는 연결 3·데이터 3·종료 4 세그먼트의 헤더, MQTT 는 여기에 CONNECT·CONNACK·PUBACK·DISCONNECT, MQTTS 는 TLS 핸드셰이크(인증서 포함 약 5KB)를 더했다.
      </div>
    </div>
  </Card>
);

const BatterySection: React.FC = () => (
  <Row gutter={12}>
    <Col span={12}>
      <Card size="small" title="설정별 배터리 수명 (8,500mAh)" extra={<Basis kind="추정" />} style={{ marginBottom: 12 }}>
        <Table size="small" pagination={false} rowKey="key" dataSource={BATTERY} columns={[
          { title: '설정', dataIndex: 'cfg', render: (v: string, r: any) => <Space size={4}><span style={cell}>{v}</span>{r.rec && <RecTag />}</Space> },
          { title: '일 소모', dataIndex: 'day', width: 80, render: (v: string) => <span style={num}>{v}</span> },
          { title: '수명', dataIndex: 'life', width: 80, render: (v: string) => <b style={cell}>{v}</b> },
          { title: '비고', dataIndex: 'note', render: (v: string) => <span style={{ ...cell, color: gray[600] }}>{v}</span> },
        ]} />
        <div style={{ fontSize: 11.5, color: gray[500], marginTop: 8, lineHeight: 1.7 }}>
          근거: Dragino 배터리 FAQ 의 S31-CB 측정값(UDP 세션 0.46mAh · 업로드 측정 0.17mAh · 대기 19.6µA), 8,500mAh · 자가방전 2%/년. 괄호는 신호 약함 추정.
          S31-CB2(19,000mAh)는 약 1.9배. 실험실 측정이라 <b>파일럿 1~2대의 배터리 전압 추세로 확정</b>한다(업링크 로그 · 장치 대장의 배터리 열).
        </div>
      </Card>
    </Col>
    <Col span={12}>
      <Card size="small" title="데이터 타입 × 통신 방식 조합 — 8시간 · N=16" extra={<Basis kind="추정" />} style={{ marginBottom: 12 }}>
        <Table size="small" pagination={false} rowKey="key" dataSource={COMBOS} columns={[
          { title: '조합', dataIndex: 'combo', width: 130, render: (v: string, r: any) => <Space size={4}><b style={cell}>{v}</b>{r.rec && <RecTag />}</Space> },
          { title: '신호 양호 (세션 · 수명)', dataIndex: 'good', width: 130, render: (v: string) => <span style={num}>{v}</span> },
          { title: '신호 약함', dataIndex: 'weak', width: 120, render: (v: string) => <span style={num}>{v}</span> },
          { title: '판단', dataIndex: 'note', render: (v: string) => <span style={{ ...cell, color: gray[600] }}>{v}</span> },
        ]} />
        <div style={{ fontSize: 12.5, lineHeight: 1.8, color: gray[700], marginTop: 8 }}>
          <b>결론</b>: 배터리 최적은 <b>UDP + HEX</b>(적용). MQTT · TCP · CoAP 는 세션이 8~11초 길어 HEX 로 바이트를 줄여도 UDP 보다 짧다.
          그보다 큰 변수는 <b>신호 세기</b>다 — 같은 조합도 약한 신호에서는 수명이 절반 이하로 준다.
          순서는 ① 설치 위치로 신호 확보(-105dBm 초과) ② 그래도 약한 하우스는 S31-CB2(19,000mAh) ③ 간격은 확인 시각이 허락하는 만큼 길게.
        </div>
      </Card>
      <Card size="small" title="배터리 수명을 정하는 요인 (영향 큰 순)">
        <Table size="small" pagination={false} rowKey={(r: any) => r[0]} dataSource={BATTERY_FACTORS} columns={[
          { title: '요인', key: 'k', width: 120, render: (_: any, r: any) => <b style={cell}>{r[0]}</b> },
          { title: '설명', key: 'v', render: (_: any, r: any) => <span style={{ ...cell, color: gray[700] }}>{r[1]}</span> },
        ]} />
      </Card>
    </Col>
  </Row>
);

/* ── AT 명령 · 블루투스 설정 ─────────────────────────────────
   근거: Dragino S31-CB/S31B-CB 사용자 매뉴얼 · -CB/-CS 공통 설정 가이드 · BLE Configuration 가이드 (2026-11-04 확인).
   권장 명령 '값'의 정본은 서버(/api/thlogger/config 의 at_commands)다 — 이 표는 각 명령의 '뜻'을 설명한다 */
const BLE_STEPS = [
  { title: '전원 · SIM', body: '케이스를 열고 나노 SIM 을 장착한다(-1T 는 1NCE SIM 이 이미 꽂혀 있다). 전원 점퍼 JP2 를 꽂으면 켜지고 빼면 꺼진다. SW1 은 Flash(동작) 위치 — ISP 는 펌웨어 업로드용이라 LED 가 꺼진 채 아무것도 하지 않는다.' },
  { title: '블루투스 깨우기', body: '버튼을 1~3초 누르거나(파랑 1회 깜빡임) 리셋 버튼을 누른다. 블루투스는 60초 동안 연결이 없으면 꺼진다 — 꺼지면 다시 누른다.' },
  { title: '앱에서 장치 찾기', body: "Android 'Devices.tool'(Google Play) · iOS Dragino 앱(App Store)을 열고 'SCAN BLUETOOTH DEVICES' → 라벨의 장치 ID(DEV EUI)와 같은 이름을 고른다." },
  { title: 'AT PIN 입력', body: "'DIALOGBOX' 를 열고 라벨의 AT PIN 6자리만 보낸다('AT+PIN=' 은 빼고). 'Password Correct' 가 오면 명령창이 열린 것이다." },
  { title: '권장 명령 입력', body: "아래 '권장 명령' 을 위에서부터 한 줄씩 보내고 각각 OK 를 확인한다(여러 줄을 한 번에 붙여 보내도 된다). 앱 기록에 남아 다음 장치에서 눌러 재사용할 수 있다." },
  { title: 'ATZ 로 재시작', body: '마지막 ATZ 를 08:00 무렵에 보내면 매일 08:00 에 업로드된다. 재시작 후 망을 찾으면 파랑이 3초 켜진다(LTE-M 첫 접속은 수십 초~수 분 걸릴 수 있다).' },
  { title: 'lab 에서 수신 확인', body: "온습도로거 → 업링크 로그에 IMEI 가 보이면 끝. 안 보이면 거절 로그(미등록 IMEI · 해석 실패)부터 본다. 장치 대장 등록이 먼저다." },
];
const AT_REC: [string, string, string][] = [
  ['AT+PRO=2,0', 'UDP · HEX 전송', '앞자리 1 CoAP · 2 UDP · 3 MQTT · 4 TCP / 뒷자리 0 HEX · 5 JSON. UDP 가 세션 최단, HEX 가 데이터 최소(JSON 의 1/5). 서버는 JSON 도 받는다'],
  ['AT+SERVADDR=<IP>,1999', '수신 서버 주소 · 포트', '도메인 대신 IP — 업로드마다 DNS 조회가 붙지 않는다. 값은 아래 복사 버튼(서버 설정값)을 쓴다'],
  ['AT+IOTMOD=0', '망 = LTE-M(eMTC) 고정', '0 eMTC · 1 NB-IoT · 2 둘 다. 1NCE 는 한국 NB-IoT 가 없어 0 으로 고정해야 검색 시간 · 배터리를 아낀다'],
  ['AT+APN=iot.1nce.net', '1NCE APN', '사용자 · 암호 없음'],
  ['AT+CLOCKLOG=1,65535,60,32', '1시간 측정 · 업로드마다 32건 동봉', '켬(1) · 시작 시각(65535 = 접속 후 시작, 0~3599 면 매시 그 초에) · 간격(분) · 동봉 건수(최대 32). 32건 = 32시간분 — 하루 간격보다 8시간 겹쳐 시계 밀림 · 지연 업로드에도 빈칸이 없다'],
  ['AT+TDC=86400', '하루 1번 업로드', '초 단위. 8시간이면 28800 + CLOCKLOG 끝값 16'],
  ['AT+REDPT=1', '재전송 켬', 'ACK 를 못 받은 업로드를 다음 ACK 때 다시 보낸다'],
  ['AT+SHTEMP=0,0', '온도 경보 업링크 끔', '최소 = 최대면 꺼진다. 켜 두면 측정마다 임계를 넘을 때 즉시 업로드 → 한낮 하우스에서 매일 추가 세션. 경보는 서버가 판정한다'],
  ['AT+SHHUM=50,50', '습도 경보 업링크 끔', '위와 같은 이유 (최소 = 최대)'],
  ['AT+GPS=0', 'GNSS 끔', '기본 꺼짐이지만 명시 — 하우스 고정 설치라 위치가 필요 없다'],
  ['AT+INTMOD=0', '외부 인터럽트 끔', '배선 없는 입력 핀의 헛 업링크를 막는다'],
  ['ATZ', '재시작 · 설정 적용', '이 시각부터 업로드 간격을 센다 → 08:00 무렵에'],
];
const AT_CHECK: [string, string][] = [
  ['AT+<명령>=?', '현재 값 조회 — 예: AT+TDC=? · AT+APN=? · AT+SERVADDR=?'],
  ['AT+<명령>?', '그 명령의 도움말'],
  ['AT+GETSENSORVALUE', '지금 측정값(온도 · 습도) — 프로브 연결 확인'],
  ['AT+LDATA', '마지막으로 올린 데이터'],
  ['AT+PLDTA=5', '저장된 최근 5건 기록'],
  ['AT+DEUI', '장치 ID 확인'],
  ['AT+GETLOG', '단말 로그 출력 — 접속 실패 원인 확인용'],
];
const AT_NET: [string, string, string][] = [
  ['AT+CSQTIME=10', '망 검색 시간(분)', '접속이 늦는 현장에서 늘린다. 길수록 실패 시 배터리 소모도 크다'],
  ['AT+QBAND=<eMTC>,<NB>', '주파수 밴드 지정', '한국 LTE-M(KT) 밴드를 1NCE 답변으로 확인한 뒤 좁힌다 — 틀리면 접속이 안 된다. 확인 전에는 건드리지 않는다'],
  ['AT+QCFG="nwscanseq",02,1', 'eMTC 우선 검색', 'BG95 모듈 명령(-CB 공통 가이드). IOTMOD=0 이면 보통 필요 없다 — 파일럿에서 확인'],
  ['AT+RXDL=1000', '다운링크 수신 대기(ms)', '장치 대장의 원격 주기 변경이 반영되지 않으면 늘려 본다'],
  ['AT+BKDNS=0,0,NULL', '정적 DNS 끔', 'IP 로 서버를 지정하므로 기본값 그대로 둔다'],
  ['AT+QSW', 'LTE 모듈 전원 토글', '모듈이 응답하지 않을 때 1회. 보통은 ATZ 로 충분'],
];
const AT_CARE: [string, string, 'bad' | 'warn' | 'info'][] = [
  ['AT+FDR', '공장 초기화 — 비밀번호까지 초기화된다. 현장에서는 쓰지 않는다', 'bad'],
  ['AT+FDR1', '비밀번호는 두고 나머지 초기화 — 설정이 꼬였을 때. 이후 권장 명령을 다시 넣는다', 'warn'],
  ['AT+CLRDTA', '단말에 저장된 기록 삭제 — 사무실 시험 데이터를 지우고 설치할 때', 'warn'],
  ['AT+PWORD=xxxxxx', 'AT PIN 변경(6자리) — 바꾸면 장치 대장 비고에 보관 위치를 적는다. 잊으면 블루투스로 못 들어간다', 'warn'],
  ['AT+PRO=2,5', 'JSON 으로 되돌리기 — HEX 에 문제가 있을 때. 서버는 두 형식을 함께 받는다', 'info'],
];
const BUTTONS: [string, string, string][] = [
  ['1~3초', '즉시 업로드 · 블루투스 켜짐', '파랑 1회'],
  ['3초 넘게', '장치 활성화 → 망 검색', '초록 빠르게 5회 → 12초 펌웨어 대기 → 망을 찾으면 파랑 3초'],
  ['5번 연속', '비활성화(딥슬립) — 보관 · 운송할 때', '빨강 5초'],
  ['리셋 버튼', '재부팅 · 블루투스 켜짐', '-'],
];
const DOWNLINK_HEX: [string, string][] = [
  ['01 01 51 80', 'TDC = 86400초(1일 1회)'],
  ['08 01 FF FF 3C 20', 'CLOCKLOG = 1,65535,60,32'],
  ['0A 00 00 00 00', '온습도 경보 끔'],
  ['04 FF', '재시작'],
];

/* ── 지점 연속성 (설치 지점 · 기기 교체) — 온습도 기록 전문가 관점 ───────────────── */
const PT_PRINCIPLES: [string, string][] = [
  ['기록의 주인은 지점', '성적서의 환경 조건은 "시험 장소의 온습도" 다. 기기는 그 값을 재는 도구라 바뀔 수 있다 — 그래서 조회 · 시험 배정 · 엑셀 · 현황 카드는 모두 지점 단위다.'],
  ['원자료는 기기에, 지점은 기간으로', '측정값은 받은 그대로 기기 기록에 쌓고, 지점 값은 설치 이력 [설치 ~ 철거) 기간으로 골라 이어 붙인다. 저장값에 지점을 박지 않으므로 설치 시각을 현장 기록대로 정정하면 과거 조회가 자동으로 맞는다(정정은 활동이력에 남는다).'],
  ['한 지점 = 한 기기 = 한 시점', '같은 시각에 한 지점에 두 기기, 한 기기가 두 지점은 서버가 거절한다. 병행 측정 중의 새 기기 값은 그 기기 기록에만 남고, 교체 시각부터 지점 기록이 된다.'],
  ['결손은 숨기지 않는다', '교체 사이의 빈 시간은 결손으로 센다. 이웃 하우스 값 · 보간으로 메우지 않는다 — 필요하면 성적서에 "기기 고장 MM-DD HH ~ MM-DD HH 결측" 주석을 단다.'],
  ['위치가 바뀌면 다른 지점', '같은 하우스라도 높이 · 위치를 옮기면 새 지점으로 등록한다. 같은 코드에 다른 위치 값이 섞이면 추세가 거짓이 된다.'],
  ['교체마다 확인 기록', '병행 측정 차이(Δ℃ · Δ%RH) · 교정 성적서 번호를 교체 기록에 남긴다 — GLP 추적성(어느 값을 어느 기기가, 그 기기는 믿을 만했는가).'],
];
const PT_SOP = [
  { title: '① 예비기 준비 (사무실)', body: '권장 AT 명령 입력 → 업로드 1~2회로 리튬전지 깨우기 → 장치 대장 등록 확인 → 기준 온습도계와 1시간 비교(±0.5℃ · ±3%RH).' },
  { title: '② 현장 병행 측정', body: '새 기기를 옛 기기 바로 옆(같은 높이 · 같은 차폐)에 2시간 이상 둔다. 고장으로 옛 기기가 죽었으면 생략하고 사유를 적는다.' },
  { title: '③ 교체 기록', body: "lab 설치 지점 → 해당 지점 '기기 교체' → 교체 시각 · 내리는 사유(고장/배터리/교정/정기 교체) · 병행 측정 차이 입력." },
  { title: '④ 옛 기기 처리', body: '내리기 직전 버튼을 1~3초 눌러 즉시 업로드 — 마지막 정기 업로드 뒤의 측정분(최대 24시간)이 서버로 올라온 것을 업링크 로그로 확인하고 전원을 끈다. 수리 · 교정이면 장치 대장 비고에 의뢰처 · 날짜, 폐기면 비활성화. 고친 기기는 예비기로 돌아온다(어느 지점에도 없는 활성 기기 = 예비기).' },
  { title: '⑤ 확인', body: '다음 08:00 업로드(기다리기 어려우면 버튼 1~3초로 즉시 업로드)에서 현황 카드가 새 기기로 \'수신 정상\' 인지, 측정 데이터 차트의 교체 점선 앞뒤가 이어지는지 본다.' },
];
const PT_KPI: [string, string, string][] = [
  ['기기 없는 지점', '0', "탭 배지 '기기 없음 n' · 현황 '확인 필요' — 그 시간은 전부 결손"],
  ['고장 → 교체 시간', '48시간 이내', '결손 상한. 예비기가 없으면 이 목표를 못 지킨다'],
  ['예비기', '20대에 2대 (10%)', "설치 지점 탭 '예비기 n대'. 교정 순환 · 동시 고장 대비"],
  ['병행 측정 차이', '±0.5℃ · ±3%RH 이내', '넘으면 새 기기를 쓰지 말고 교정 확인'],
  ['지점별 결손률', '월 1% 미만', '측정 데이터 \'결손(정시)\' — 기기 교체 공백 포함'],
  ['교정 주기', '연 1회 기준기 비교', "교정 중에는 예비기로 교체 기록(사유 '교정' → 돌아오면 '교정 복귀')"],
];

const PointSection: React.FC = () => (
  <>
    <Alert type="success" showIcon style={{ marginBottom: 12 }}
      message={<span><b>설치 지점 구조 적용</b> — 기기가 고장 나 예비기로 바꿔도 <b>지점의 온습도 기록 · 시험 배정 · 엑셀이 끊기지 않고 이어진다</b></span>}
      description={<span style={{ fontSize: 12 }}>lab 온습도로거 → <b>설치 지점</b> 탭에서 지점(H01-1 …)을 등록하고 기기를 설치 · 교체한다. 현황 카드와 측정 데이터는 지점 단위이며,
        차트에는 기기 교체 시각이 점선으로, 엑셀에는 사용 기기(기간)와 값마다 측정 기기가 적힌다.</span>} />
    <Row gutter={12} style={{ overflowX: 'hidden' }}>
      <Col span={12}>
        <Card size="small" title="구조" style={{ marginBottom: 12 }}>
          <div style={{ fontFamily: font.mono, fontSize: 11.5, lineHeight: 1.7, background: gray[50], borderRadius: 6, padding: '8px 10px', whiteSpace: 'pre', overflowX: 'auto' }}>{
`설치 지점 H07-1 (7번 하우스 중앙 · 1.2m · 차폐)
 ├ 설치 이력  로거-07  06-07 00:00 ~ 08-07 15:00  내림: 고장
 │            (08-06 08:00 업로드 후 무응답 → 30시간 결손)
 ├ 설치 이력  로거-21  08-07 15:00 ~ 현재          설치: 교체
 └ 시험 배정  DEMO-26-05  07-17 ~ 09-05

지점 값 = 각 기기 측정값 중 [설치 ~ 철거) 안의 것
현황 · 측정 데이터 · 엑셀 · 시험 환경 = 지점 기준`}
          </div>
          <div style={{ fontSize: 11.5, color: gray[500], marginTop: 6 }}>예시는 더미 데이터의 실제 교체 사례(H07-1 고장 · H13-1 배터리 선제 교체)와 같은 모양이다.</div>
        </Card>
        <Card size="small" title="원칙 (온습도 기록 · GLP 관점)" style={{ marginBottom: 12 }}>
          <Table size="small" pagination={false} showHeader={false} rowKey={(r: any) => r[0]} dataSource={PT_PRINCIPLES} columns={[
            { key: 'k', width: 150, render: (_: any, r: any) => <b style={cell}>{r[0]}</b> },
            { key: 'v', render: (_: any, r: any) => <span style={{ ...cell, color: gray[700] }}>{r[1]}</span> },
          ]} />
        </Card>
      </Col>
      <Col span={12}>
        <Card size="small" title="기기 교체 절차 (SOP)" style={{ marginBottom: 12 }}>
          <Steps direction="vertical" size="small" current={-1} items={PT_SOP.map(x => ({ title: <b style={{ fontSize: 12.5 }}>{x.title}</b>, description: <span style={{ fontSize: 12, color: gray[600] }}>{x.body}</span> }))} />
        </Card>
        <Card size="small" title="관리 지표" style={{ marginBottom: 12 }}>
          <Table size="small" pagination={false} rowKey={(r: any) => r[0]} dataSource={PT_KPI} columns={[
            { title: '지표', key: 'k', width: 130, render: (_: any, r: any) => <b style={cell}>{r[0]}</b> },
            { title: '목표', key: 't', width: 130, render: (_: any, r: any) => <span style={num}>{r[1]}</span> },
            { title: '보는 곳 · 이유', key: 'w', render: (_: any, r: any) => <span style={{ ...cell, color: gray[600] }}>{r[2]}</span> },
          ]} />
        </Card>
      </Col>
    </Row>
  </>
);

/* ── 사용자 가이드: 시작하기 · 유심 장착 ─────────────────────────
   근거: Dragino S31-CB 사용자 매뉴얼(1.6 버튼 · LED, 1.8 핀 · 스위치 · 유심 방향) · -CB/-CS 공통 설정 가이드(2.1 망 접속: 전원 끄기 → 유심 → 전원 켜기 → APN) ·
   NSPH01 매뉴얼('모듈을 빼고 유심을 넣는다'). 공식 동영상은 없다(2026-11-04 Dragino 위키 · 웹 검색 확인).
   사진은 Dragino 공식 사이트 원본을 그대로 불러온다(사본을 두지 않는다 — 주소가 바뀌면 링크로 대체 표시) */
const DRAGINO = {
  board: 'https://wiki.dragino.com/assets/images/image-20240617104620-1-f20d3cce3408046bd3c4b7a1caa4e405.png',
  power: 'https://docs.dragino.com/assets/images/image-20240602220856-1-583fd3795dcd032080c46297288fed9a.png',
  simdir: 'https://docs.dragino.com/assets/images/image-20230808205045-1-eb53c2634ab39074fdb19c2e8f098891.png',
  product: 'https://wiki.dragino.com/assets/images/image-20230819110632-6-3bb1f0daa2882d8b88be8a415fd46c7a.png',
  manual: 'https://wiki.dragino.com/docs/NB-IoT/temperature-humidity-sensors/s31-cb/',
  general: 'https://docs.dragino.com/docs/NB-IoT/general-configuration/cb-cs-models-nb-iot-lte-m/',
};
const OfficialImg: React.FC<{ src: string; alt: string; caption: React.ReactNode; marks?: { x: number; y: number; n: string; label: string }[] }> = ({ src, alt, caption, marks }) => {
  const [failed, setFailed] = useState(false);
  return (
    <figure style={{ margin: 0 }}>
      {failed ? (
        <div style={{ padding: 16, background: gray[50], border: `1px dashed ${gray[300]}`, borderRadius: 8, fontSize: 12.5, color: gray[600] }}>
          공식 사진을 불러오지 못했습니다 — <a href={DRAGINO.manual} target="_blank" rel="noreferrer">Dragino S31-CB 매뉴얼</a>에서 확인하세요.
        </div>
      ) : (
        <div style={{ position: 'relative', borderRadius: 8, overflow: 'hidden', border: `1px solid ${gray[200]}`, background: '#fff' }}>
          <img src={src} alt={alt} loading="lazy" onError={() => setFailed(true)} style={{ display: 'block', width: '100%', height: 'auto' }} />
          {marks?.map(m => (
            <Tooltip key={m.n} title={m.label}>
              <span style={{ position: 'absolute', left: `${m.x}%`, top: `${m.y}%`, transform: 'translate(-50%, -50%)', width: 22, height: 22, borderRadius: 11,
                background: '#e11d48', color: '#fff', fontSize: 12, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 0 0 2px #fff, 0 2px 6px rgba(0,0,0,.35)', cursor: 'help' }}>{m.n}</span>
            </Tooltip>))}
        </div>
      )}
      <figcaption style={{ fontSize: 11.5, color: gray[500], marginTop: 4, lineHeight: 1.6 }}>{caption}</figcaption>
    </figure>
  );
};

/* 본체 사진 위 번호 — 좌표는 공식 사진(792×451) 기준 % */
const BOARD_MARKS = [
  { x: 70.8, y: 25, n: 'A', label: 'A 전원 점퍼(JP2) — 빼면 꺼지고 꽂으면 켜진다' },
  { x: 74.5, y: 33, n: 'B', label: 'B 배터리 커넥터 — 전선을 잡아당기지 않는다' },
  { x: 65.6, y: 33.5, n: 'C', label: 'C 버튼 — 1~3초 업로드 · 블루투스, 3초 이상 활성화, 5번 연속 딥슬립' },
  { x: 59, y: 27, n: 'D', label: 'D LED — 초록 · 파랑 · 빨강으로 상태 표시' },
  { x: 47.8, y: 36, n: 'E', label: 'E 모드 스위치(SW1) — FLASH/RUN 쪽이어야 동작한다(ISP 는 펌웨어 업데이트용)' },
  { x: 82.5, y: 53.5, n: 'F', label: 'F 통신 모듈 고정 나사 — 풀면 모듈이 비스듬히 올라온다(유심은 모듈 뒷면)' },
  { x: 96, y: 46.5, n: 'G', label: 'G 안테나 단자(SMA) — 손으로 끝까지 돌려 조인다' },
];

const SIM_STEPS_UI = [
  { t: '준비', d: '나노 유심(1NCE 3-in-1 카드에서 가장 작은 크기로 떼어 낸다) · 십자 드라이버 · 마른 천. 실내의 건조한 책상에서 한다 — 비 오는 날 현장에서 열지 않는다. 손으로 금속(문고리 등)을 한 번 만져 정전기를 뺀다.',
    warn: "'-1T' 모델은 1NCE 유심이 이미 꽂혀 출고된다 — 7번(전원 켜기)부터 하면 된다." },
  { t: '덮개 열기', d: '덮개 모서리 나사를 풀고 덮개를 들어 올린다. 덮개 안쪽 홈의 고무 패킹이 빠지거나 꼬이지 않게 덮개를 뒤집어 깨끗한 곳에 둔다.' },
  { t: '전원 끄기 (A)', d: "전원 점퍼(JP2)를 뽑는다 — 공식 순서가 '전원 끄기 → 유심 넣기 → 전원 켜기' 다. 점퍼는 작아서 잃어버리기 쉬우니 덮개 안에 둔다.",
    warn: '전원이 켜진 채 유심을 넣고 빼면 모듈이 유심을 인식하지 못하거나 손상될 수 있다.' },
  { t: '통신 모듈 빼기 (F)', d: '모듈 끝의 고정 나사(F)를 푼다. 모듈이 비스듬히 들리면 커넥터 반대 방향으로 살짝 당겨 뺀다. 금색 단자(커넥터 쪽 가장자리)는 손으로 만지지 않는다.' },
  { t: '유심 넣기', d: '모듈 뒷면의 유심 홀더에 나노 유심을 넣는다. 방향은 아래 공식 사진처럼 잘린 모서리 위치를 맞추고, 금속 접점이 홀더(기판) 쪽을 향하게 해 끝까지 밀어 넣는다. 걸리면 억지로 밀지 말고 방향을 다시 본다.',
    warn: "공식 방향 사진은 같은 규격의 다른 모듈(BC660K) 사진이다. S31-CB(BG95)의 홀더 위치 · 방향은 첫 기기에서 사진을 찍어 이 칸에 추가한다." },
  { t: '모듈 다시 끼우기', d: '모듈을 약 30° 기울여 커넥터에 끝까지 밀어 넣고(단자가 안 보일 만큼), 눌러 내려 고정 나사를 조인다. 덜 들어가면 망을 못 찾는다.' },
  { t: '전원 켜기 (A · E)', d: '전원 점퍼를 다시 꽂는다. 모드 스위치(E)가 FLASH/RUN 쪽인지 확인한다 — ISP 쪽이면 LED 도 통신도 동작하지 않는다.' },
  { t: '망 접속 확인 (C · D)', d: "버튼을 3초 넘게 누른다 → 초록 LED 빠르게 5번 → 약 12초 뒤 파랑 1번 → 망을 찾으면 파랑이 3초 켜진다. LTE-M 첫 접속은 수십 초~수 분 걸릴 수 있다. 처음이면 블루투스로 권장 AT 명령(APN 포함)을 넣는다('AT 명령 · 블루투스')." },
  { t: '안테나 · 닫기 (G)', d: '안테나를 손으로 끝까지 돌려 조인다. 패킹이 홈에 바르게 들어갔는지 보고 덮개 나사를 대각선 순서로 고르게 조인다. 프로브 케이블 쪽 방수 너트(케이블 글랜드)도 조인다 — 하우스는 습기가 많다.' },
  { t: 'lab 에서 수신 확인', d: "온습도로거 → 업링크 로그에서 이 기기 IMEI 의 수신과 신호(dBm)를 본다. 거절 로그에 '미등록' 으로 보이면 통신은 된 것이니 장치 대장에 등록하면 된다." },
];
const SIM_CAUTIONS: [string, string][] = [
  ['전원 끄고 작업', '유심은 반드시 전원 점퍼를 뺀 상태에서 넣고 뺀다'],
  ['정전기', '작업 전에 금속을 만져 정전기를 빼고, 기판 부품 · 금색 단자를 만지지 않는다'],
  ['IoT 유심만', '휴대폰 유심이 아니라 NB-IoT/LTE-M 유심(1NCE) 이어야 한다 — 크기는 나노'],
  ['방향', '잘린 모서리 방향을 사진과 맞춘다. 거꾸로 넣으면 신호가 99(접속 실패)로 나온다'],
  ['반복 탈착 금지', '유심을 자주 넣었다 빼면 홀더 접점이 닳아 인식 불량이 생긴다(모듈 제조사 포럼 사례) — 한 번에 바르게'],
  ['모드 스위치', 'SW1 은 FLASH/RUN — 실수로 ISP 로 밀면 아무 반응이 없다'],
  ['배터리 커넥터', '전선이 아니라 하얀 커넥터 몸통을 잡는다. 정품 팩(배터리 + 보조 커패시터)만 쓴다'],
  ['방수', '패킹 · 케이블 글랜드 · 덮개 나사가 방수의 전부다. 현장에서 비 · 이슬이 있을 때 열지 않는다'],
];
const SIM_TROUBLE: [string, string, string][] = [
  ['LED 가 전혀 안 켜진다', '전원 점퍼 빠짐 · SW1 이 ISP · 배터리 커넥터 빠짐', '점퍼를 꽂고 SW1 을 FLASH 로, 커넥터 확인 후 리셋 버튼'],
  ['파랑 3초가 안 켜진다', '유심 방향 · 모듈 덜 꽂힘 · 유심 미활성 · LTE-M 커버리지', '모듈을 다시 끼우고 유심 방향 확인 → 1NCE 포털에서 활성 상태 → 창가 · 높은 곳에서 다시'],
  ['블루투스로 신호가 99', '망을 못 찾음 (0~31 이 정상)', 'APN(iot.1nce.net) · IOTMOD=0 확인, 안테나 조임 확인'],
  ['업링크 로그에 안 보인다', '서버 주소 오타 · 장치 미등록', "거절 로그에 IMEI 가 있으면 장치 대장 등록, 없으면 'AT 명령' 의 서버 주소 확인"],
  ['블루투스 연결이 안 된다', '블루투스는 버튼을 누른 뒤 60초만 켜진다', '버튼 1~3초 → 60초 안에 앱에서 연결 · AT PIN 6자리'],
];

const SimInstallSection: React.FC = () => (
  <>
    <Alert type="warning" showIcon style={{ marginBottom: 12 }}
      message={<span><b>처음 하는 분도 이 순서대로</b> — 전원 끄기 → 모듈 빼기 → 유심 넣기 → 모듈 끼우기 → 전원 켜기 → 파랑 3초 확인. 약 10분.</span>}
      description={<span style={{ fontSize: 12 }}>Dragino 공식 매뉴얼 · 공통 설정 가이드의 순서와 공식 사진을 바탕으로 정리했다. 공식 동영상은 없어(2026-11-04 확인) 첫 기기를 장착할 때 사내 영상을 찍어 이 칸에 추가할 것을 권한다.
        '-1T' 모델은 유심이 이미 장착돼 있다.</span>} />
    <Row gutter={12} style={{ overflowX: 'hidden' }}>
      <Col span={13}>
        <Card size="small" title="본체 안쪽 — 위치 (번호에 마우스를 올리면 설명)" style={{ marginBottom: 12 }}>
          <OfficialImg src={DRAGINO.board} alt="S31-CB 본체 내부 — 전원 점퍼, 모드 스위치, BG95 통신 모듈" marks={BOARD_MARKS}
            caption={<>출처: <a href={DRAGINO.manual} target="_blank" rel="noreferrer">Dragino S31-CB 매뉴얼 1.8 (핀 · 스위치 · 유심 방향)</a>. 번호는 SAPPT 표시.</>} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2px 12px', fontSize: 12, marginTop: 8 }}>
            {BOARD_MARKS.map(m => <div key={m.n}><b style={{ color: '#e11d48' }}>{m.n}</b> {m.label.slice(2)}</div>)}
          </div>
        </Card>
        <Row gutter={12}>
          <Col span={12}>
            <Card size="small" title="전원 끄기 · 켜기 (A)" style={{ marginBottom: 12 }}>
              <OfficialImg src={DRAGINO.power} alt="전원 점퍼 위치 — 빼면 꺼짐, 꽂으면 켜짐" caption={<>'Take out Jumper to power off / Put Jumper to power on'. 출처: <a href={DRAGINO.general} target="_blank" rel="noreferrer">-CB 공통 설정 가이드 2.1</a></>} />
            </Card>
          </Col>
          <Col span={12}>
            <Card size="small" title="유심 방향" style={{ marginBottom: 12 }}>
              <OfficialImg src={DRAGINO.simdir} alt="나노 유심 삽입 방향 — 잘린 모서리 위치" caption={<>잘린 모서리(동그라미) 방향 · 화살표 방향으로. 사진은 BC660K 모듈 — 같은 규격, S31-CB 는 첫 기기에서 확인. 출처: <a href={DRAGINO.general} target="_blank" rel="noreferrer">-CB 공통 설정 가이드</a></>} />
            </Card>
          </Col>
        </Row>
      </Col>
      <Col span={11}>
        <Card size="small" title="장착 순서" style={{ marginBottom: 12 }}>
          <Steps direction="vertical" size="small" current={-1} items={SIM_STEPS_UI.map((x, i) => ({
            title: <b style={{ fontSize: 13 }}>{i + 1}. {x.t}</b>,
            description: <div style={{ fontSize: 12.5, color: gray[700], lineHeight: 1.7 }}>{x.d}
              {x.warn && <div style={{ marginTop: 3, color: '#92400e', background: '#fffbeb', borderRadius: 4, padding: '3px 8px' }}>⚠ {x.warn}</div>}</div>,
          }))} />
        </Card>
      </Col>
    </Row>
    <Row gutter={12} style={{ overflowX: 'hidden' }}>
      <Col span={10}>
        <Card size="small" title="주의사항" style={{ marginBottom: 12 }}>
          <Table size="small" pagination={false} showHeader={false} rowKey={(r: any) => r[0]} dataSource={SIM_CAUTIONS} columns={[
            { key: 'k', width: 110, render: (_: any, r: any) => <b style={{ ...cell, color: '#b91c1c' }}>{r[0]}</b> },
            { key: 'v', render: (_: any, r: any) => <span style={{ ...cell, color: gray[700] }}>{r[1]}</span> },
          ]} />
        </Card>
      </Col>
      <Col span={14}>
        <Card size="small" title="안 될 때" style={{ marginBottom: 12 }}>
          <Table size="small" pagination={false} rowKey={(r: any) => r[0]} dataSource={SIM_TROUBLE} columns={[
            { title: '증상', key: 's', width: 150, render: (_: any, r: any) => <b style={cell}>{r[0]}</b> },
            { title: '원인 후보', key: 'c', width: 190, render: (_: any, r: any) => <span style={{ ...cell, color: gray[600] }}>{r[1]}</span> },
            { title: '조치', key: 'f', render: (_: any, r: any) => <span style={{ ...cell, color: gray[700] }}>{r[2]}</span> },
          ]} />
        </Card>
        <Card size="small" title="영상 · 원문">
          <div style={{ fontSize: 12.5, lineHeight: 1.85, color: gray[700] }}>
            <div>· 공식 동영상: <b>없음</b> (Dragino 위키 · 유튜브 검색 2026-11-04) — 첫 기기 장착 때 휴대폰으로 순서대로 찍어 사내 공유 위치를 이 칸에 적는다.</div>
            <div>· <a href={DRAGINO.manual} target="_blank" rel="noreferrer">S31-CB 사용자 매뉴얼</a> — 1.6 버튼 · LED, 1.8 핀 · 스위치 · 유심 방향</div>
            <div>· <a href={DRAGINO.general} target="_blank" rel="noreferrer">-CB/-CS 공통 설정 가이드</a> — 2.1 망 접속(전원 끄기 → 유심 → 전원 켜기 → APN)</div>
          </div>
        </Card>
      </Col>
    </Row>
  </>
);

const USER_DAILY = [
  { t: '08:00 이후 — 현황', d: "온습도로거 → 현황. 카드마다 지금 온습도와 48시간 추이가 보인다. '확인 필요' 를 눌러 이상 지점만 본다." },
  { t: '이상이 있으면 — 장치 대장', d: '이상 장치가 위로 올라오고 이름 아래에 이유가 적혀 있다(수신 지연 · 누락 · 배터리 · 신호). 아래 표대로 조치한다.' },
  { t: '성적서 · 보고 — 측정 데이터', d: "왼쪽 목록에서 지점을 고르고 기간에서 '시험 기간' 을 누른 뒤 엑셀. 기기를 바꿨어도 지점 기록은 이어진다." },
];
const USER_TABS: [string, string][] = [
  ['현황', '하우스(지점)별 지금 온습도 · 추이 · 상태. 배치 · 색은 담당자가 정한다'],
  ['측정 데이터', '지점을 골라 기간별 차트 · 통계 · 엑셀. 점선은 기기 교체 시각'],
  ['설치 지점', '하우스의 측정 위치와 지금 설치된 기기. 기기 교체 · 시험 배정은 여기서'],
  ['장치 대장', '기기 목록. 이상 장치가 강조되고 최근 14일 수신 띠가 보인다'],
  ['업링크 · 거절 로그', '기기가 보낸 원문과 해석 · 저장하지 않은 패킷(미등록 등). 문제 진단용'],
];
const USER_ACTIONS: [string, string, string][] = [
  ['수신 지연 (08:00 업로드가 없다)', '하루는 기다린다 — 다음 날 08:00 에 놓친 데이터가 재전송된다', '이틀째도 없으면 현장에서 LED · 안테나 · 전원 점퍼 확인'],
  ['누락 · 지연이 잦다', '신호가 약한 곳일 가능성이 크다', '본체를 높은 곳 · 금속에서 먼 곳으로, 그래도 약하면 담당자에게'],
  ['배터리 주의 · 교체', '3.3V 아래는 남은 기간이 짧다는 뜻', '교체 배터리(정품 팩) 준비 → 담당자와 교체 일정'],
  ['기기 고장', '지점 기록은 예비기로 이어 갈 수 있다', "'지점 연속성 · 기기 교체' 순서대로 예비기 설치 · 교체 기록"],
  ['값이 이상하다(튐 · 고정)', '프로브 · 케이블 손상 또는 햇빛 직사', '복사 차폐 덮개 · 케이블 확인, 기준 온습도계와 비교'],
];
const UserStartSection: React.FC = () => (
  <>
    <Alert type="info" showIcon style={{ marginBottom: 12 }}
      message={<span><b>이 시스템이 하는 일</b> — 하우스마다 놓인 온습도로거가 <b>1시간마다 재고, 매일 08:00 에 하루치를 서버로 보낸다</b>. 담당자는 아침에 lab 에서 확인만 하면 된다.</span>}
      description={<span style={{ fontSize: 12 }}>기기를 처음 받았다면 '유심 장착 · 기기 열기' → 'AT 명령 · 블루투스' → '구매 후 절차' 순서로 본다. 설계 · 수치 근거는 위의 '개발자 가이드' 로 바꾸면 보인다.</span>} />
    <Row gutter={12} style={{ overflowX: 'hidden' }}>
      <Col span={12}>
        <Card size="small" title="매일 확인 (1~2분)" style={{ marginBottom: 12 }}>
          <Steps direction="vertical" size="small" current={-1} items={USER_DAILY.map(x => ({ title: <b style={{ fontSize: 13 }}>{x.t}</b>, description: <span style={{ fontSize: 12.5, color: gray[700] }}>{x.d}</span> }))} />
        </Card>
        <Card size="small" title="화면 안내" style={{ marginBottom: 12 }}>
          <Table size="small" pagination={false} showHeader={false} rowKey={(r: any) => r[0]} dataSource={USER_TABS} columns={[
            { key: 'k', width: 120, render: (_: any, r: any) => <b style={cell}>{r[0]}</b> },
            { key: 'v', render: (_: any, r: any) => <span style={{ ...cell, color: gray[700] }}>{r[1]}</span> },
          ]} />
        </Card>
      </Col>
      <Col span={12}>
        <Card size="small" title="이럴 때는" style={{ marginBottom: 12 }}>
          <Table size="small" pagination={false} rowKey={(r: any) => r[0]} dataSource={USER_ACTIONS} columns={[
            { title: '상황', key: 's', width: 150, render: (_: any, r: any) => <b style={cell}>{r[0]}</b> },
            { title: '뜻', key: 'm', render: (_: any, r: any) => <span style={{ ...cell, color: gray[600] }}>{r[1]}</span> },
            { title: '할 일', key: 'a', render: (_: any, r: any) => <span style={{ ...cell, color: gray[800] }}>{r[2]}</span> },
          ]} />
        </Card>
        <Card size="small" title="LED 읽는 법 (버튼을 눌렀을 때)">
          <div style={{ fontSize: 12.5, lineHeight: 1.9, color: gray[700] }}>
            <div><Tag color="blue" style={{ margin: '0 6px 0 0' }}>파랑 1번</Tag>1~3초 — 지금 업로드 · 블루투스 60초</div>
            <div><Tag color="green" style={{ margin: '0 6px 0 0' }}>초록 5번 → 파랑 3초</Tag>3초 넘게 — 켜고 망 접속 성공</div>
            <div><Tag color="red" style={{ margin: '0 6px 0 0' }}>빨강 5초</Tag>5번 연속 — 딥슬립(보관 · 운송)</div>
            <div style={{ color: gray[500], fontSize: 12 }}>출처: Dragino S31-CB 매뉴얼 1.6</div>
          </div>
        </Card>
      </Col>
    </Row>
  </>
);

/* 명령어를 소스 코드처럼 — 어두운 바탕 · 고정폭 · 토큰 색(2026-11-04 사용자 요청).
   AT+ 접두 = 하늘 · 명령 이름 = 흰색 굵게 · = , 구분 = 회색 · 값 = 노랑 · ? 조회 = 초록 · <자리표시> = 분홍 · HEX 바이트 = 노랑 */
const CODE = { bg: '#0f172a', bar: '#1e293b', fg: '#e2e8f0', dim: '#64748b', pre: '#7dd3fc', val: '#fbbf24', q: '#86efac', ph: '#f9a8d4', cm: '#94a3b8' };
const tokenizeCmd = (t: string): React.ReactNode[] => {
  if (/^([0-9A-F]{2}\s?)+$/i.test(t.trim())) return t.split(' ').map((b, i) => <span key={i} style={{ color: CODE.val }}>{b}{i < t.split(' ').length - 1 ? ' ' : ''}</span>);
  const out: React.ReactNode[] = [];
  const re = /(AT\+|AT(?=\b|$))|([A-Z][A-Z0-9_]*(?=[=?]|$|\s))|(<[^>]+>)|([=,;·])|(\?)|([^=,;?<·]+)/g;
  let m: RegExpExecArray | null; let i = 0;
  while ((m = re.exec(t)) !== null) {
    const [tok] = m;
    const st: React.CSSProperties = m[1] ? { color: CODE.pre } : m[2] ? { color: CODE.fg, fontWeight: 700 } : m[3] ? { color: CODE.ph, fontStyle: 'italic' }
      : m[4] ? { color: CODE.dim } : m[5] ? { color: CODE.q, fontWeight: 700 } : { color: CODE.val };
    out.push(<span key={i++} style={st}>{tok}</span>);
  }
  return out;
};
const Cmd: React.FC<{ t: string; small?: boolean }> = ({ t, small }) => (
  <code style={{ fontFamily: font.mono, fontSize: small ? 11 : 11.5, background: CODE.bg, color: CODE.fg, borderRadius: 5, padding: '1px 6px',
    whiteSpace: 'nowrap', lineHeight: '18px', display: 'inline-block' }}>{tokenizeCmd(t)}</code>
);
/* 터미널 창 — 줄 번호 · 명령 · 주석, 줄마다 복사 */
const CodeBlock: React.FC<{ title: string; lines: { cmd: string; note?: string }[]; onCopyAll?: () => void }> = ({ title, lines, onCopyAll }) => (
  <div style={{ background: CODE.bg, borderRadius: 8, overflow: 'hidden', border: '1px solid #0b1222' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px', background: CODE.bar }}>
      {['#f87171', '#fbbf24', '#4ade80'].map(c => <span key={c} style={{ width: 9, height: 9, borderRadius: 5, background: c, opacity: 0.85 }} />)}
      <span style={{ fontFamily: font.mono, fontSize: 11.5, color: CODE.cm, marginLeft: 6, flex: 1 }}>{title}</span>
      {onCopyAll && <button type="button" onClick={onCopyAll} className="thl-code-copy" style={{ fontSize: 11.5, color: CODE.fg, background: 'rgba(255,255,255,.08)', border: '1px solid rgba(255,255,255,.15)', borderRadius: 5, padding: '1px 8px', cursor: 'pointer' }}>
        <CopyOutlined /> 전체 복사</button>}
    </div>
    <div style={{ padding: '8px 0', fontFamily: font.mono, fontSize: 12.5, lineHeight: '22px', overflowX: 'auto' }}>
      {lines.map((l, i) => (
        <div key={i} className="thl-code-line" style={{ display: 'flex', alignItems: 'center', padding: '0 10px 0 0', whiteSpace: 'nowrap' }}>
          <span style={{ width: 34, flex: 'none', textAlign: 'right', paddingRight: 12, color: CODE.dim, userSelect: 'none' }}>{i + 1}</span>
          <span>{tokenizeCmd(l.cmd)}</span>
          {l.note && <span style={{ color: CODE.cm, marginLeft: 14, fontStyle: 'italic' }}>{'// '}{l.note}</span>}
          <Tooltip title="이 줄 복사"><button type="button" aria-label={`${l.cmd} 복사`} onClick={() => copy(l.cmd)} className="thl-code-linecopy"
            style={{ marginLeft: 'auto', paddingLeft: 12, background: 'none', border: 'none', color: CODE.cm, cursor: 'pointer' }}><CopyOutlined /></button></Tooltip>
        </div>))}
    </div>
    <style>{`.thl-code-line:hover{background:rgba(255,255,255,.05)} .thl-code-linecopy{opacity:0} .thl-code-line:hover .thl-code-linecopy,.thl-code-linecopy:focus-visible{opacity:1}
      .thl-code-copy:hover{background:rgba(255,255,255,.16)!important}`}</style>
  </div>
);
/* 권장 명령의 주석 — 명령 이름으로 AT_REC 의 '뜻' 을 찾는다(값은 서버 설정이 정본) */
const noteOf = (cmd: string) => {
  const name = cmd.split('=')[0];
  return AT_REC.find(r => r[0].split('=')[0] === name)?.[1];
};

const AtSection: React.FC = () => {
  const [cfg, setCfg] = useState<any | null>(null);
  useEffect(() => { thloggerAPI.config().then(r => setCfg(r.data)).catch(() => {}); }, []);
  const cmds: string[] = cfg?.at_commands || [];
  const kv = (rows: [string, string][], w = 190) => (
    <Table size="small" pagination={false} showHeader={false} rowKey={(r: any) => r[0]} dataSource={rows} columns={[
      { key: 'k', width: w, render: (_: any, r: any) => <Cmd t={r[0]} /> },
      { key: 'v', render: (_: any, r: any) => <span style={{ ...cell, color: gray[700] }}>{r[1]}</span> },
    ]} />
  );
  return (
    <>
      <Alert type="info" showIcon style={{ marginBottom: 12 }}
        message={<span><b>설정은 휴대폰 블루투스로 한다</b> — 케이블 없이 하우스 현장에서 바로. 준비물: 휴대폰 앱 · 라벨의 <b>AT PIN 6자리</b> · 장치 대장에 IMEI 등록</span>}
        description={<span style={{ fontSize: 12 }}>PC 는 USB-블루투스 어댑터나 UART(USB-TTL) 로도 되지만 현장에서는 휴대폰이 가장 간단하다.
          AT PIN 은 라벨에 <Cmd t="AT+PIN=<6자리>" small /> 로 인쇄돼 있다 — 라벨 사진을 외부에 공유하지 않는다.</span>} />
      <Row gutter={12}>
        <Col span={10}>
          <Card size="small" title="블루투스 설정 순서" style={{ marginBottom: 12 }}>
            <Steps direction="vertical" size="small" current={-1} items={BLE_STEPS.map((st, i) => ({
              status: 'wait' as const,
              icon: <span style={{ width: 22, height: 22, borderRadius: 11, background: '#4f46e5', color: '#fff', fontSize: 12, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>{i + 1}</span>,
              title: <span style={{ fontSize: 13, fontWeight: 700 }}>{st.title}</span>,
              description: <span style={{ fontSize: 12, color: gray[600], lineHeight: 1.7 }}>{st.body}</span>,
            }))} />
          </Card>
          <Card size="small" title="버튼 · LED" style={{ marginBottom: 12 }}>
            <Table size="small" pagination={false} rowKey={(r: any) => r[0]} dataSource={BUTTONS} columns={[
              { title: '누르기', key: 'k', width: 76, render: (_: any, r: any) => <b style={cell}>{r[0]}</b> },
              { title: '동작', key: 'a', render: (_: any, r: any) => <span style={cell}>{r[1]}</span> },
              { title: 'LED', key: 'l', render: (_: any, r: any) => <span style={{ ...cell, color: gray[600] }}>{r[2]}</span> },
            ]} />
            <div style={{ fontSize: 11.5, color: gray[500], marginTop: 6 }}>동작 중(측정 · 송신)에는 버튼이 먹지 않을 수 있다 — 끝난 뒤 다시 누른다.</div>
          </Card>
        </Col>
        <Col span={14}>
          <Card size="small" title="권장 명령 — 위에서부터 순서대로" style={{ marginBottom: 12 }}
            extra={<Button size="small" icon={<CopyOutlined />} disabled={!cmds.length} onClick={() => copy(cmds.join('\n'))}>서버 설정값 전체 복사</Button>}>
            <Table size="small" pagination={false} rowKey={(r: any) => r[0]} dataSource={AT_REC} columns={[
              { title: '명령', key: 'c', width: 210, render: (_: any, r: any) => <Cmd t={r[0]} /> },
              { title: '뜻', key: 'm', width: 160, render: (_: any, r: any) => <b style={cell}>{r[1]}</b> },
              { title: '설명', key: 'd', render: (_: any, r: any) => <span style={{ ...cell, color: gray[600] }}>{r[2]}</span> },
            ]} />
            {cmds.length > 0 && (
              <div style={{ marginTop: 10 }}>
                <CodeBlock title="S31-CB · 블루투스 콘솔 — 서버 설정값" lines={cmds.map(c => ({ cmd: c, note: noteOf(c) }))} onCopyAll={() => copy(cmds.join('\n'))} />
              </div>
            )}
            <div style={{ fontSize: 11.5, color: gray[500], marginTop: 6 }}>
              복사되는 값은 서버 설정(수신 주소 · 권장 주기)에서 온다 — 이 화면의 표와 다르면 복사 값이 맞다. 명령마다 줄바꿈이 필요하다(앱의 자동 줄바꿈 켬).
            </div>
          </Card>
          <Row gutter={12}>
            <Col span={12}>
              <Card size="small" title="확인 · 조회 명령" style={{ marginBottom: 12 }}>{kv(AT_CHECK, 150)}</Card>
            </Col>
            <Col span={12}>
              <Card size="small" title="주의 명령" style={{ marginBottom: 12 }}>
                {AT_CARE.map(([c, d, sev]) => (
                  <div key={c} style={{ fontSize: 12, lineHeight: 1.65, marginBottom: 6 }}>
                    <Cmd t={c} />{' '}
                    <span style={{ color: sev === 'bad' ? '#b91c1c' : sev === 'warn' ? '#b45309' : gray[600] }}>{sev === 'bad' ? '✖' : sev === 'warn' ? '△' : 'ℹ'}</span>
                    <div style={{ color: gray[600] }}>{d}</div>
                  </div>
                ))}
              </Card>
            </Col>
          </Row>
        </Col>
      </Row>
      <Row gutter={12}>
        <Col span={14}>
          <Card size="small" title="접속이 안 될 때 — 망 관련 명령" style={{ marginBottom: 12 }}>
            <Table size="small" pagination={false} rowKey={(r: any) => r[0]} dataSource={AT_NET} columns={[
              { title: '명령', key: 'c', width: 210, render: (_: any, r: any) => <Cmd t={r[0]} /> },
              { title: '뜻', key: 'm', width: 140, render: (_: any, r: any) => <b style={cell}>{r[1]}</b> },
              { title: '언제', key: 'd', render: (_: any, r: any) => <span style={{ ...cell, color: gray[600] }}>{r[2]}</span> },
            ]} />
            <ol style={{ margin: '8px 0 0', paddingLeft: 18, fontSize: 12, lineHeight: 1.8, color: gray[700] }}>
              <li><Cmd t="AT+APN=?" small /> · <Cmd t="AT+IOTMOD=?" small /> 로 값부터 확인 (오타가 가장 흔하다)</li>
              <li>1NCE 포털에서 SIM 이 '활성'이고 접속 시도 기록이 있는지 확인 — 기록이 없으면 무선 구간(위치 · 밴드), 있으면 서버 구간</li>
              <li><Cmd t="AT+GETLOG" small /> 로그를 앱에서 내보내 시스템 담당에게 전달</li>
            </ol>
          </Card>
        </Col>
        <Col span={10}>
          <Card size="small" title="여러 대 한 번에 · 원격 변경" style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 12.5, lineHeight: 1.8, color: gray[700] }}>
              <b>일괄 설정</b>: Devices.tool 오른쪽 위 메뉴 → 'Multi-device Config Json'. 공통 명령 묶음 + 장치별 명령(ID 로 구분, 비밀번호 칸 선택)을 담은 JSON 파일을 고른 뒤
              장치를 하나씩 연결해 'Process' 를 누른다. 20대 구축 때 PIN 입력 · 명령 반복을 줄인다(파일 형식은 앱 예시 파일을 따른다).
            </div>
            <div style={{ fontSize: 12.5, lineHeight: 1.8, color: gray[700], marginTop: 8 }}>
              <b>설치 후 원격 변경</b>: 장치 대장의 '주기 변경' 이 다음 업로드의 응답에 실어 보낸다 — 단말이 HEX 모드면 아래 HEX 코드를 명령마다 한 패킷으로, JSON 모드면 JSON Config 로. HEX 다운링크 코드는:
            </div>
            <div style={{ marginTop: 4 }}>{kv(DOWNLINK_HEX, 150)}</div>
          </Card>
        </Col>
      </Row>
      <div style={{ fontSize: 11.5, color: gray[500], marginBottom: 4 }}>
        출처: Dragino S31-CB/S31B-CB 사용자 매뉴얼 · -CB/-CS 공통 설정 가이드 · BLE Configuration 가이드. 기본값 · 망 명령(QBAND · nwscanseq · INTMOD)은 파일럿 실기기로 확인 후 확정한다.
      </div>
    </>
  );
};

const DeviceSection: React.FC = () => {
  const [cfg, setCfg] = useState<any | null>(null);
  useEffect(() => { thloggerAPI.config().then(r => setCfg(r.data)).catch(() => {}); }, []);
  const cmds: string[] = cfg?.at_commands || [];
  return (
    <>
      <Alert type="info" showIcon icon={<DownloadOutlined />} style={{ marginBottom: 12 }}
        message={<Space wrap size={8}>
          <b>PC 시뮬레이터 다운로드</b>
          <a href={CLIENT_ZIP} download>thlogger_client.zip</a>
          <Text code style={{ fontSize: 12 }}>{window.location.origin}{CLIENT_ZIP}</Text>
          <Button type="text" size="small" icon={<CopyOutlined />} onClick={() => copy(`${window.location.origin}${CLIENT_ZIP}`)}>주소 복사</Button>
        </Space>} />
      <Row gutter={12}>
        <Col span={12}>
          <Card size="small" title="수신 서버" style={{ marginBottom: 12 }}>
            <Descriptions size="small" column={1} bordered items={[
              { key: 'ep', label: '엔드포인트', children: <Space><Text code>{cfg?.endpoint || '-'}</Text><Button type="text" size="small" icon={<CopyOutlined />} onClick={() => copy(cfg?.endpoint || '')} /></Space> },
              { key: 'pr', label: '프로토콜', children: cfg?.protocol || '-' },
              { key: 'lv', label: '수신 지연 판정', children: <div style={{ fontSize: 12 }}>주의: {cfg?.levels?.WARN}<br />미수신: {cfg?.levels?.CRIT}</div> },
              { key: 'bt', label: '배터리 경고', children: `${cfg?.battery_warn_v ?? 3.0}V 미만` },
            ]} />
          </Card>
          <Card size="small" title="단말 설정 — BLE 앱 또는 USB 콘솔에서 순서대로 입력" style={{ marginBottom: 12 }}>
            <pre style={{ margin: 0, fontFamily: font.mono, fontSize: 12, lineHeight: 1.8, background: gray[50], padding: 10, borderRadius: 6 }}>{cmds.join('\n')}</pre>
            <Space style={{ marginTop: 8 }} wrap>
              <Button size="small" icon={<CopyOutlined />} onClick={() => copy(cmds.join('\n'))}>전체 복사</Button>
            </Space>
            <div style={{ fontSize: 12, color: gray[600], marginTop: 8 }}>명령별 뜻 · 블루투스 연결 순서 · 버튼/LED · 문제 해결은 <b>'AT 명령 · 블루투스'</b> 카테고리에 있다.</div>
          </Card>
        </Col>
        <Col span={12}>
          <Card size="small" title="신규 장치 적용 절차" style={{ marginBottom: 12 }}>
            <ol style={{ margin: 0, paddingLeft: 18, fontSize: 13, lineHeight: 1.9 }}>
              <li>1NCE SIM 장착 · <Text code>AT+APN=iot.1nce.net</Text> — 구매 · 활성화는 '1NCE 구매 절차' 탭</li>
              <li><b>장치 대장 → 등록</b>: 라벨의 IMEI 15자리, 장치명, 하우스 코드, 측정/업로드 주기</li>
              <li>왼쪽 AT 명령을 단말에 입력 (<Text code>AT+SERVADDR</Text> 이 이 서버) → 08:00 무렵 <Text code>ATZ</Text> 재시작</li>
              <li>버튼을 눌러 즉시 업로드 → <b>업링크 로그</b>에 1~2분 안에 보이면 성공. 알림센터에 '첫 수신' 알림이 뜬다</li>
              <li>안 보이면 <b>거절 로그</b>: 미등록 IMEI(오타) · 해석 실패(다른 모델 · 펌웨어 배치 변경 → 원문을 보고 AT+PRO=2,5 로 임시 복귀) · 아무 기록도 없음(망/APN/서버주소)</li>
              <li>하우스에 설치 후 <b>시험 배정</b>으로 시험번호와 기간을 연결 → 측정 데이터·엑셀이 성적서 환경조건 근거가 된다</li>
              <li>운영 중 주기를 바꿀 때는 장치 대장 작업 열의 <b>전송 버튼(주기 변경)</b> → 다음 업링크 때 기기로 전달되고 반영 여부가 '주기' 열에 표시된다</li>
            </ol>
          </Card>
          <Card size="small" title="PC 수신 확인 도구 사용법" style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 12.5, lineHeight: 1.8, color: gray[700] }}>
              Python 3 설치(‘Add python.exe to PATH’ 체크) → zip 압축 해제 → <Text code>check_reception.bat</Text> 실행.
              인자 없이 실행하면 이 PC 전용 가상 장치(<Text code>PC-XXXXXX</Text>)로 보낸다. 처음엔 미등록이라 [FAIL] 이 정상이고,
              <b> 거절 로그에 그 IMEI 가 보이면 통신은 성공</b>한 것이다. 장치 대장에 등록하고 다시 실행하면 [OK].
              IMEI 확인은 <Text code>check_reception.bat id</Text>. 회사 망이 UDP 를 막으면 스마트폰 테더링으로 실행한다.
            </div>
          </Card>
        </Col>
      </Row>
    </>
  );
};

/* ── 통신망 (NB-IoT · LTE-M) · 1NCE 유심 ─────────────────────
   근거 (2026-11-03 검토):
   · 규격 수치 — 3GPP Rel-13/14 (Cat-NB1·NB2 / Cat-M1) 공개 사양 값. 최대 결합손실(MCL) NB-IoT 164dB · LTE-M 156dB
   · 1NCE — MDS테크놀로지 구매가(21,000원 VAT 별도 · 10년 · 500MB · 배송비 5,000원), Deutsche Telekom IoT 로밍 파트너 목록
     (대한민국 = KT LTE-M, 'TBC' 표기, NB-IoT 항목 없음). 한국 커버리지는 1NCE 공식 확인 전까지 '미확정'으로 둔다
   · 2026-11-04 요금제를 1NCE 10년 500MB 로 확정(국내 통신사 요금제 삭제) → 한국에서는 LTE-M(KT 망 로밍)만 쓴다
   · 배터리·비용 — 추정 모델 / 환율·카드값·배송비에 따라 변동. 장치 대장 배지의 데이터 한도(dataQuota)와 같은 값 */
const RAT = [
  ['대역폭', '200kHz', '1.4MHz', '-'],
  ['최대 속도', '수십 kbps (NB2 약 127kbps)', '약 1Mbps', '업로드 1회 1.2KB — 둘 다 충분'],
  ['커버리지 (MCL)', '164dB — 지하·차폐 공간까지', '156dB — NB-IoT 보다 약 8dB 얕다', 'NB-IoT 우세 · 하우스 구석·농지 외곽에 유리'],
  ['지연', '1.6~10초', '10~15ms', '하루 1번 올리는 온습도에는 무의미'],
  ['이동성 · 핸드오버', '없음 (정지 단말용)', '있음', '하우스 고정 설치 — 필요 없다'],
  ['순간 전류', '낮다 (협대역)', '높다 (광대역)', '배터리 · SPC 커패시터 부담은 NB-IoT 가 작다'],
  ['세션당 무선 시간', '길다 (저속 · 반복 전송)', '짧다', '좋은 신호에서는 비슷, 약한 신호에서 NB-IoT 는 반복 전송으로 늘지만 접속은 유지'],
  ['PSM · eDRX', '지원 (망 설정 확인)', '지원 (망 설정 확인)', '-CB 는 세션 사이 모듈을 꺼(대기 19.6µA) 수명에 영향이 작다'],
  ['펌웨어 원격 갱신(FOTA)', '느리다 (수백 KB 를 저속으로)', '현실적', 'S31-CB 는 현장 FOTA 를 쓰지 않는다'],
  ['국제 로밍', '제한적 (국가·사업자별)', '넓다', '1NCE 가 한국에서 LTE-M 만 주는 이유'],
  ['1NCE 한국 제공', '없음', 'KT 망 로밍 (공식 확인 필요)', '→ AT+IOTMOD=0 으로 LTE-M 고정. S31-CB(-CB, BG95-M2) 는 LTE-M 지원'],
  ['요금', '1NCE 10년 500MB 동일', '1NCE 10년 500MB 동일', '망과 무관하게 SIM 1장 21,000원(VAT 별도)'],
];
const NETPLAN: [string, React.ReactNode][] = [
  ['SIM · 요금', '1NCE IoT Lifetime Flat — 21,000원(VAT 별도) 1회 · 10년 · 500MB · MDS테크놀로지 구매 (국내 통신사 요금제 사용 안 함)'],
  ['망', 'LTE-M (Cat-M1) · KT 망 로밍 — AT+IOTMOD=0(eMTC)으로 고정해 NB-IoT 검색에 시간 · 배터리를 쓰지 않게 한다'],
  ['APN', 'iot.1nce.net (사용자 · 암호 없음)'],
  ['전송', 'UDP + HEX · 1일 1회(08:00) · N=32 · 재전송 켬 · 경보 · GPS · 인터럽트 끔 — 망을 바꿔도 그대로'],
  ['서버', '변경 없음 — IMEI 로 식별하고 ACK · 주기 변경이 같은 UDP 소켓 응답이라 1NCE NAT 를 통과한다. UDP 1999 에 출발지 IP 허용목록을 걸지 않는다'],
  ['장치 대장', "통신사/망 = '1NCE', 회선번호 칸에 ICCID — 최적화 배지의 데이터 한도는 500MB / 120개월 ≈ 월 4.2MB"],
];
const ONCE_FACTS = [
  ['요금', 'SIM 1장 21,000원(VAT 별도) · 배송비 5,000원 — 10년 · 500MB. 소진 · 만료 시 충전 · 연장(조건은 MDS테크놀로지 확인)'],
  ['망 구조', 'Deutsche Telekom 코어 + 각국 로밍 파트너. 국내에서는 해외 로밍 단말로 붙는다(IMSI 901-40 국제 대역)'],
  ['한국 커버리지', 'DT 로밍 파트너 목록에 KT LTE-M 이 \'TBC\' 로 있고 NB-IoT 는 없다 — 1NCE 지원팀 공식 커버리지 목록으로 확인 필요'],
  ['데이터 경로', '단말 → KT 무선 → 1NCE 코어(유럽) → 인터넷 → SAPPT 수신기(UDP 1999). 왕복 지연 약 0.3초 추가, 출발지 IP 는 1NCE NAT(해외)'],
  ['APN', 'iot.1nce.net (사용자·암호 없음)'],
  ['관리', '1NCE 포털 · REST API: SIM 활성/정지, 데이터 잔량, 접속 이력, SMS 발송, 사용량 경보'],
  ['구매 · 관리', 'MDS테크놀로지(국내 공급사)에서 구매 · 국내 결제. 사용량 · 개통 정보는 1NCE 홈페이지(포털)에서 관리'],
];
const ONCE_PROS = [
  ['국내 개통 절차 없음', '국내 통신사 법인 가입 서류 · 회선 개통 · 단말 IMEI 등록을 거치지 않는다 — MDS테크놀로지에서 사서 꽂아 쓴다'],
  ['10년 정액 · 데이터 여유', '권장 구성(UDP+HEX) 22.1KB/월 × 120개월 ≈ 2.6MB → 500MB 의 0.5%. JSON(85.6KB/월)도 10MB 로 들어간다'],
  ['10년 정액', '1대 10년 21,000원(VAT 별도, 연 2,100원) — 월 청구 · 약정 · 연체 · 해지 관리가 없다'],
  ['일괄 관리 API', '20대 SIM 상태 · 잔량 · 마지막 접속을 한 화면에서. 분실 SIM 즉시 정지'],
  ['다중 망 · 해외 이동', '국내 다른 사업자 로밍(협약 시) · 해외 시험 포장에 같은 SIM 사용'],
  ['SMS 250건', '현장 방문 없이 SMS 로 재부팅·설정 명령 전달 가능성(S31-CB 의 SMS 명령 지원은 매뉴얼 확인)'],
];
const ONCE_CONS = [
  ['한국 커버리지 미확정', 'NB-IoT 로밍 없음 → LTE-M 만. KT LTE-M 로밍이 실제로 열려 있는지 구매 전 서면 확인 필수(1NCE 구매 절차 ①)', 'bad'],
  ['영구 로밍 리스크', '국내 사업자가 장기 체류 로밍 IoT 를 제한·차단할 수 있다(정책 변경 시 20대가 동시에 끊긴다)', 'bad'],
  ['약전계 불리', 'LTE-M 은 NB-IoT 보다 커버리지가 약 8dB 얕다 — NB-IoT 로 잡히는 하우스가 LTE-M 로밍으로는 안 잡힐 수 있다', 'warn'],
  ['배터리', 'LTE-M 로밍은 첫 접속이 길 수 있다 — 세션이 길어지면 수명이 준다(1일 1회 신호 양호 14.8년 → 약함 8.7년, S31-CB2 는 16.0년)', 'warn'],
  ['전파법 · 인증', '단말 KC 전파인증은 SIM 과 무관하게 필요. 해외 로밍 회선 상시 사용의 국내 규정 적합성 확인', 'warn'],
  ['지원 · 계약', '구매는 MDS테크놀로지(국내)지만 망 · 포털은 1NCE(독일) — 장애 문의 경로를 구매 때 정해 둔다. 10년 선불이라 공급사 존속 리스크가 있다', 'warn'],
  ['데이터 경유지', '측정값이 유럽 코어를 거친다 — 온습도는 민감정보가 아니지만 GLP 감사에서 경로 설명이 필요할 수 있다', 'info'],
];
const ONCE_APPLY = [
  ['단말 설정', 'AT+IOTMOD=0(LTE-M 고정 — NB-IoT 자동 검색에 시간 · 배터리 낭비 방지) · AT+APN=iot.1nce.net · 나머지 AT 명령은 권장 구성 그대로 (사용자 가이드 \'AT 명령 · 블루투스\' 에 반영됨)'],
  ['서버 (변경 없음)', '수신기는 출발지 IP 를 보지 않고 IMEI 로 식별한다 → 1NCE NAT 를 거쳐도 그대로 동작. ACK · 주기 변경(다운링크)도 같은 UDP 소켓의 응답이라 NAT 를 통과한다'],
  ['하지 말 것', '수신 포트(UDP 1999)에 출발지 IP 허용 목록을 걸지 않는다 — 1NCE NAT 주소는 바뀐다. 1NCE OS 의 UDP 수집기 · Data Streamer 로 경로를 바꾸지 않는다(ACK · 다운링크 · 중복 제거를 서버가 하고 있다)'],
  ['장치 대장', "통신사/망 = '1NCE' 로 등록, 회선번호 칸에 ICCID. 최적화 배지의 데이터 한도는 1NCE 기준(500MB / 120개월 ≈ 월 4.2MB)"],
  ['감시', '1NCE 포털 사용량 경보를 켜 둔다. 수신 지연 판정 · 배터리 경고는 망과 무관하게 기존 그대로'],
];
const ONCE_GATES = [
  '1NCE 서면 확인 완료 — 한국 LTE-M(KT 로밍) · 로밍 중 PSM/eDRX · 장기 로밍 제한 (1NCE 구매 절차 ①)',
  'S31-CB(-CB) LTE-M 지원 밴드 ↔ 한국 LTE-M 밴드 일치 · KC 전파인증 확인',
  '파일럿 2대(1NCE)를 같은 하우스에 2~3주 — 수신율 · 신호(dBm) · 배터리 전압 기울기 · 1NCE 포털 사용량',
  '판정: 결손 0 이고 배터리 기울기가 계산(1일 1회 14.8년, 약한 신호 8.7년) 범위면 본구축 20장 주문',
];
const SEV_COLOR: Record<string, string> = { bad: '#b91c1c', warn: '#b45309', info: gray[500] };

const NetworkSection: React.FC = () => (
  <>
    <Alert type="info" showIcon style={{ marginBottom: 12 }}
      message={<span><b>적용</b> — <b>1NCE SIM</b> 으로 한국에서는 <b>LTE-M (KT 망 로밍)</b>에 접속한다. 1NCE 는 한국 NB-IoT 를 제공하지 않는다.</span>}
      description={<span style={{ fontSize: 12 }}>하우스 온습도는 정지 · 소량 · 저빈도라 LTE-M 의 장점(속도 · 지연 · 이동성)은 쓰지 않는다. 대신 NB-IoT 보다 커버리지가 약 8dB 얕고 순간 전류가 크다 —
        <b> 설치 위치의 신호 확인</b>이 NB-IoT 때보다 더 중요하다. 신호가 약하면 본체 위치 · 높이부터 조정한다.</span>} />
    <Card size="small" title="NB-IoT vs LTE-M — 1NCE 로 LTE-M 을 쓸 때 얻는 것과 잃는 것" style={{ marginBottom: 12 }}>
      <Table size="small" pagination={false} rowKey={(r: any) => r[0]} dataSource={RAT} scroll={{ x: 'max-content' }} columns={[
        { title: '항목', key: 'k', width: 130, render: (_: any, r: any) => <b style={cell}>{r[0]}</b> },
        { title: 'NB-IoT (Cat-NB)', key: 'nb', width: 210, render: (_: any, r: any) => <span style={cell}>{r[1]}</span> },
        { title: 'LTE-M (Cat-M1) · 적용', key: 'm', width: 230, render: (_: any, r: any) => <span style={cell}>{r[2]}</span> },
        { title: '온습도로거에 미치는 영향', key: 'i', render: (_: any, r: any) => <span style={{ ...cell, color: gray[700] }}>{r[3]}</span> },
      ]} />
    </Card>
    <Card size="small" title="적용 통신 구성" style={{ marginBottom: 12 }}>
      <Table size="small" pagination={false} showHeader={false} rowKey={(r: any) => r[0]} dataSource={NETPLAN} columns={[
        { key: 'k', width: 100, render: (_: any, r: any) => <b style={cell}>{r[0]}</b> },
        { key: 'v', render: (_: any, r: any) => <span style={{ ...cell, color: gray[700] }}>{r[1]}</span> },
      ]} />
      <div style={{ marginTop: 8 }}>
        <Space wrap>
          <Text code style={{ fontSize: 11.5 }}>AT+APN=iot.1nce.net</Text>
          <Button size="small" icon={<CopyOutlined />} onClick={() => copy('AT+APN=iot.1nce.net')}>복사</Button>
          <Text code style={{ fontSize: 11.5 }}>AT+IOTMOD=0</Text>
          <span style={{ fontSize: 11.5, color: gray[500] }}>0 = LTE-M(eMTC) · 1 = NB-IoT · 2 = 둘 다 (Dragino -CB 공통 설정 가이드)</span>
        </Space>
      </div>
    </Card>
    <Card size="small" title="현장 설치 순서">
      <ol style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, lineHeight: 1.9, color: gray[700] }}>
        <li>설치 후 버튼 업로드 → 업링크 로그의 신호(dBm) 확인. <b>-105dBm 초과</b>면 확정.</li>
        <li>-105dBm 이하: 본체를 높이 · 금속 구조물에서 떼어 · 하우스 측면 쪽으로 옮겨 다시 확인 (프로브만 안쪽에 둔다).</li>
        <li>그래도 접속이 안 되거나 끊기면: 1NCE 포털에서 해당 SIM 의 접속 이력(망 · 시각)을 확인하고 1NCE 지원팀에 문의한다.</li>
        <li>보온 커튼을 닫은 겨울 야간 신호가 가장 나쁘다 — 그 시간대 업링크 신호로 최종 판단한다.</li>
      </ol>
      <div style={{ fontSize: 11.5, color: gray[500], marginTop: 6 }}>
        <Basis kind="공개요금" /> MDS테크놀로지 구매가 · <Basis kind="추정" /> 커버리지 · 배터리 영향은 규격 값과 배터리 모델 기반, 파일럿에서 실측으로 확정
      </div>
    </Card>
  </>
);

const OnceSection: React.FC = () => (
  <>
    <Alert type="success" showIcon style={{ marginBottom: 12 }}
      message={<span><b>적용</b> — 온습도로거 통신 요금제는 <b>1NCE 10년 · 500MB</b> (MDS테크놀로지 · SIM 1장 21,000원 VAT 별도 · 배송비 5,000원)</span>}
      description={<span style={{ fontSize: 12 }}>권장 구성(UDP+HEX)의 10년 사용량은 약 2.6MB(500MB 의 0.5%)라 데이터 걱정이 없고 월 요금이 없다.
        다만 한국에서는 <b>NB-IoT 로밍이 없고 KT LTE-M 로밍도 공식 미확정</b>이다 — 구매 전 서면 확인과 파일럿 판정이 적용 조건이다.
        20대가 한 해외 사업자의 로밍 정책에 함께 묶이는 위험은 아래 '위험 · 관리' 로 관리한다.</span>} />
    <Card size="small" title="1NCE 개요" style={{ marginBottom: 12 }}>
      <Table size="small" pagination={false} showHeader={false} rowKey={(r: any) => r[0]} dataSource={ONCE_FACTS} columns={[
        { key: 'k', width: 100, render: (_: any, r: any) => <b style={cell}>{r[0]}</b> },
        { key: 'v', render: (_: any, r: any) => <span style={{ ...cell, color: gray[700] }}>{r[1]}</span> },
      ]} />
    </Card>
    <Row gutter={12}>
      <Col span={12}>
        <Card size="small" title="장점" style={{ marginBottom: 12 }}>
          {ONCE_PROS.map(([t, d]) => (
            <div key={t} style={{ fontSize: 12.5, lineHeight: 1.7, marginBottom: 6 }}>
              <b style={{ color: '#15803d' }}>＋ {t}</b><div style={{ color: gray[600], fontSize: 12 }}>{d}</div>
            </div>
          ))}
        </Card>
      </Col>
      <Col span={12}>
        <Card size="small" title="위험 · 관리" style={{ marginBottom: 12 }}>
          {ONCE_CONS.map(([t, d, sev]) => (
            <div key={t} style={{ fontSize: 12.5, lineHeight: 1.7, marginBottom: 6 }}>
              <b style={{ color: SEV_COLOR[sev] }}>{sev === 'bad' ? '✖' : sev === 'warn' ? '△' : 'ℹ'} {t}</b><div style={{ color: gray[600], fontSize: 12 }}>{d}</div>
            </div>
          ))}
        </Card>
      </Col>
    </Row>
    <Card size="small" title="적용 방법 — 통신 · 설정" style={{ marginBottom: 12 }}>
      <Table size="small" pagination={false} showHeader={false} rowKey={(r: any) => r[0]} dataSource={ONCE_APPLY} columns={[
        { key: 'k', width: 120, render: (_: any, r: any) => <b style={cell}>{r[0]}</b> },
        { key: 'v', render: (_: any, r: any) => <span style={{ ...cell, color: gray[700] }}>{r[1]}</span> },
      ]} />
    </Card>
    <Card size="small" title="적용 판정 (순서대로)">
      <Steps direction="vertical" size="small" current={-1} items={ONCE_GATES.map((g, i) => ({ title: <span style={{ fontSize: 12.5 }}>{g}</span>, status: 'wait' as const, icon: <span style={{ fontSize: 12, fontWeight: 700, color: gray[500] }}>{i + 1}</span> }))} />
      <div style={{ fontSize: 11.5, color: gray[500], marginTop: 4 }}>
        <Basis kind="공개요금" /> MDS테크놀로지 구매가 · DT IoT 로밍 파트너 목록 — 커버리지는 '보장 없음'으로 공시되므로 반드시 서면 확인
      </div>
    </Card>
  </>
);

/* ── 카테고리 내비게이션 ───────────────────────────────────
   카테고리가 14개라 가로 탭은 줄바꿈되고 무엇이 어디 속하는지 안 보인다 → 왼쪽 그룹형 목록(아이콘 · 제목 · 한 줄 설명).
   목록과 본문은 각자 내부 스크롤(높이 100% 정책) — 긴 본문을 내려도 목록은 제자리에 있다. 마지막 선택은 브라우저에 기억 */
/* 가이드 두 갈래 (2026-11-04): 사용자 = 현장 · 매일 쓰는 사람(설치 · 유심 · 블루투스 · 교체 · 확인), 개발자 = 설계 · 수치 근거 · 서버 설정 */
const NAV_USER = [
  { group: '시작', items: [
    { key: 'ustart', label: '시작하기', sub: '매일 확인 · 화면 · 이럴 때는', icon: <DashboardOutlined />, el: <UserStartSection /> },
  ] },
  { group: '기기 준비', items: [
    { key: 'siminstall', label: '유심 장착 · 기기 열기', sub: '사진 · 순서 · 주의사항', icon: <IdcardOutlined />, el: <SimInstallSection /> },
    { key: 'at', label: 'AT 명령 · 블루투스', sub: '앱 연결 · 명령 · 버튼/LED', icon: <MobileOutlined />, el: <AtSection /> },
    { key: 'purchase', label: '구매 후 절차', sub: '입고 → 20대 운영', icon: <CheckSquareOutlined />, el: <PurchaseSection /> },
  ] },
  { group: '현장 운영', items: [
    { key: 'point', label: '지점 연속성 · 기기 교체', sub: '교체 순서 · 관리 지표', icon: <SwapOutlined />, el: <PointSection /> },
  ] },
];
const NAV_DEV = [
  { group: '설계 요약', items: [
    { key: 'summary', label: '요약 · 적용 구성', sub: '한 화면 결론', icon: <DashboardOutlined />, el: <SummarySection /> },
    { key: 'expert', label: '전문가 검토', sub: '주의사항 · 고려사항', icon: <SafetyCertificateOutlined />, el: <ExpertSection /> },
  ] },
  { group: '운영 설계', items: [
    { key: 'extreme', label: '극한 절감', sub: 'UDP+HEX · Dragino 실측 · CB2', icon: <ThunderboltOutlined />, el: <ExtremeSection /> },
    { key: 'save', label: '절전 · 데이터 최적화', sub: '조치 12가지 · 배지 기준', icon: <RocketOutlined />, el: <SaveSection /> },
    { key: 'interval', label: '전송 간격 · 시각', sub: '1일 1회 · 08:00 · N32', icon: <ClockCircleOutlined />, el: <IntervalSection /> },
    { key: 'plan', label: '요금제', sub: '1NCE · 10년 500MB', icon: <DollarOutlined />, el: <PlanSection /> },
    { key: 'volume', label: '데이터량 실측', sub: '31일 시뮬레이터', icon: <BarChartOutlined />, el: <VolumeSection /> },
  ] },
  { group: '기술 선택', items: [
    { key: 'payload', label: '데이터 타입', sub: 'JSON · HEX', icon: <CodeOutlined />, el: <PayloadSection /> },
    { key: 'protocol', label: '통신 방식', sub: 'UDP · MQTT · MQTTS', icon: <ApiOutlined />, el: <ProtocolSection /> },
    { key: 'network', label: '통신망', sub: 'LTE-M (1NCE 로밍)', icon: <WifiOutlined />, el: <NetworkSection /> },
    { key: 'once', label: '1NCE 적용 검토', sub: '장단점 · 위험 · 판정', icon: <GlobalOutlined />, el: <OnceSection /> },
    { key: 'battery', label: '배터리', sub: '조합 · 신호 영향', icon: <ThunderboltOutlined />, el: <BatterySection /> },
  ] },
  { group: '구매 · 서버', items: [
    { key: 'buy', label: '구매 구성', sub: '완제품 · 배터리 · 프로브', icon: <ShoppingCartOutlined />, el: <BuySection /> },
    { key: 'sim', label: '1NCE 구매 절차', sub: '확인 → 주문 → 활성화', icon: <IdcardOutlined />, el: <SimSection /> },
    { key: 'device', label: '장치 설정 · 시뮬레이터', sub: '서버 주소 · PC 도구', icon: <SettingOutlined />, el: <DeviceSection /> },
  ] },
];
const NAV_AUD_KEY = 'sappt_thlogger_guide_aud';
const NAV_KEY = 'sappt_thlogger_guide_tab';

/* 사용자 · 개발자 가이드의 색 — 한눈에 어느 쪽을 보고 있는지 알 수 있게 목록 배경 · 선택 표시 · 제목 아이콘 · 꼬리표까지 같은 색으로 (2026-11-04)
   사용자 = 청록(현장), 개발자 = 인디고(시스템 · lab 기본색). 흰 글자 대비 4.5 이상인 진한 색만 채움에 쓴다 */
const AUD = {
  user: { label: '사용자 가이드', who: '현장 · 매일 쓰는 분', desc: '설치 · 유심 · 블루투스 · 매일 확인 · 기기 교체', icon: <TeamOutlined />,
    c: '#0f766e', ink: '#134e4a', soft: '#ccfbf1', tint: '#f0fdfa', line: '#99f6e4' },
  dev: { label: '개발자 가이드', who: '시스템 담당', desc: '설계 근거 · 수치 · 통신 · 요금 · 서버 설정', icon: <ApiOutlined />,
    c: '#4338ca', ink: '#312e81', soft: '#e0e7ff', tint: '#f5f6ff', line: '#c7d2fe' },
} as const;

const ThLoggerGuide: React.FC = () => {
  const { ref: fillRef, height: fillHeight } = useFillHeight(300);
  // 사용자 / 개발자 — 기본은 사용자. 마지막 선택을 기억한다(카테고리도 각자)
  const [aud, setAud] = useState<'user' | 'dev'>(() => { try { return localStorage.getItem(NAV_AUD_KEY) === 'dev' ? 'dev' : 'user'; } catch { return 'user'; } });
  const NAV = aud === 'user' ? NAV_USER : NAV_DEV;
  const T = AUD[aud];
  const all = NAV.flatMap(g => g.items);
  const [active, setActive] = useState<string>(() => { try { return localStorage.getItem(NAV_KEY) || ''; } catch { return ''; } });
  const pick = (k: string) => { setActive(k); try { localStorage.setItem(NAV_KEY, k); } catch { /* 저장 불가 환경 */ } };
  const switchAud = (a: 'user' | 'dev') => { setAud(a); try { localStorage.setItem(NAV_AUD_KEY, a); } catch { /* 저장 불가 환경 */ } };
  const cur = all.find(i => i.key === active) || all[0];
  const count = { user: NAV_USER.flatMap(g => g.items).length, dev: NAV_DEV.flatMap(g => g.items).length };
  return (
    <div ref={fillRef} style={{ height: fillHeight, display: 'flex', gap: 12, minHeight: 0 }}>
      <nav aria-label="설정 가이드 카테고리" style={{
        width: 232, flex: '0 0 232px', display: 'flex', flexDirection: 'column', background: T.tint, border: `1px solid ${T.line}`,
        borderRadius: 10, overflow: 'hidden', transition: 'background .15s, border-color .15s',
      }}>
        {/* 가이드 전환 — 큰 버튼 두 개. 선택된 쪽은 그 색으로 채운다 */}
        <div role="tablist" aria-label="가이드 종류" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, padding: 8, background: '#fff', borderBottom: `1px solid ${T.line}` }}>
          {(['user', 'dev'] as const).map(k => {
            const t = AUD[k]; const on = aud === k;
            return (
              <button key={k} type="button" role="tab" aria-selected={on} onClick={() => switchAud(k)} className="thl-aud-btn"
                style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2, padding: '8px 9px', borderRadius: 8, cursor: 'pointer', textAlign: 'left',
                  border: `1.5px solid ${on ? t.c : gray[200]}`, background: on ? t.c : '#fff', color: on ? '#fff' : gray[600],
                  boxShadow: on ? `0 2px 8px ${t.c}55` : 'none', transition: 'background .12s, box-shadow .12s',
                }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 13, fontWeight: 800 }}>
                  <span style={{ fontSize: 14, color: on ? '#fff' : t.c }}>{t.icon}</span>{t.label.replace(' 가이드', '')}
                </span>
                <span style={{ fontSize: 11, color: on ? '#fff' : gray[500], whiteSpace: 'nowrap' }}>{k === 'user' ? '현장용' : '시스템 담당'} · {count[k]}개</span>
              </button>);
          })}
        </div>
        {/* 지금 보고 있는 가이드 — 색 띠 + 설명 */}
        <div style={{ padding: '8px 12px 6px', borderLeft: `4px solid ${T.c}`, margin: '8px 8px 2px', background: '#fff', borderRadius: 6 }}>
          <div style={{ fontSize: 12.5, fontWeight: 800, color: T.ink }}>{T.label}</div>
          <div style={{ fontSize: 11, color: gray[500], lineHeight: 1.5 }}>{T.desc}</div>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '4px 6px 8px' }}>
          {NAV.map(g => (
            <div key={g.group} style={{ marginBottom: 4 }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: T.c, letterSpacing: 0.4, padding: '8px 10px 4px', opacity: 0.85 }}>{g.group}</div>
              {g.items.map(it => {
                const on = it.key === cur.key;
                return (
                  <button key={it.key} type="button" onClick={() => pick(it.key)} aria-current={on ? 'page' : undefined}
                    className="thl-guide-nav-item"
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left', cursor: 'pointer',
                      padding: '7px 10px', margin: '1px 0', border: 'none', borderRadius: 7,
                      background: on ? '#fff' : 'transparent', boxShadow: on ? `inset 3px 0 0 ${T.c}, 0 1px 3px rgba(15,23,42,.08)` : 'none',
                    }}>
                    <span style={{
                      width: 26, height: 26, flex: '0 0 26px', borderRadius: 6, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      background: on ? T.c : T.soft, color: on ? '#fff' : T.c, fontSize: 13,
                    }}>{it.icon}</span>
                    <span style={{ minWidth: 0 }}>
                      <span style={{ display: 'block', fontSize: 13, fontWeight: on ? 800 : 600, color: on ? T.ink : gray[800] }}>{it.label}</span>
                      <span style={{ display: 'block', fontSize: 11, color: gray[500], whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{it.sub}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          ))}
        </div>
        <style>{`.thl-guide-nav-item:hover{background:rgba(255,255,255,.75)} .thl-guide-nav-item[aria-current=page]:hover{background:#fff}
          .thl-guide-nav-item:focus-visible,.thl-aud-btn:focus-visible{outline:2px solid ${T.c};outline-offset:1px}
          .thl-aud-btn[aria-selected=false]:hover{border-color:${gray[300]};background:${gray[50]}}`}</style>
      </nav>
      <section style={{ flex: 1, minWidth: 0, overflowY: 'auto', overflowX: 'hidden', paddingRight: 4 }}>
        {/* 본문 머리 — 가이드 색 아이콘 + 꼬리표로 어느 가이드의 화면인지 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '2px 0 10px', padding: '6px 10px', borderRadius: 8, background: T.tint, borderLeft: `4px solid ${T.c}` }}>
          <span style={{ width: 30, height: 30, borderRadius: 8, background: T.c, color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}>{cur.icon}</span>
          <div style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 15.5, fontWeight: 800, color: gray[900] }}>{cur.label}</span>
              <span style={{ fontSize: 11, fontWeight: 800, color: '#fff', background: T.c, borderRadius: 999, padding: '1px 8px', lineHeight: '18px', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                {T.icon}{T.label}</span>
            </div>
            <div style={{ fontSize: 12, color: gray[500] }}>{NAV.find(g => g.items.includes(cur))?.group} · {cur.sub}</div>
          </div>
        </div>
        {cur.el}
      </section>
    </div>
  );
};

export default ThLoggerGuide;
